import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import { useTheme } from 'styled-components';
import { Button, Icon, SearchableSelect, H1, StatusBadge, Input, SectionHeading } from '@tviklax/ui-components';
import { useGraphQLMutation, useGraphQLQuery } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useCurrencyFormatter } from '../../../hooks/useCurrencyFormatter';
import { useRole } from '../../../context/RoleContext';
import { logAudit } from '../../../shared/audit/audit';
import { Payment } from './types';
import {
  PaymentsContainer,
  PageHeader,
  Subtitle,
  OrderBar,
  OrderMeta,
  OrderMetaItem,
  OrderMetaLabel,
  OrderMetaValue,
  ListContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  AmountCell,
  ActionCell,
  EmptyState,
  DrawerOverlay,
  DrawerPanel,
  DrawerHeader,
  DrawerTitle,
  DrawerClose,
  DrawerContent,
  DrawerSection,
  DrawerLabel,
  DrawerValue,
  DrawerActions,
  ModalOverlay,
  ModalPanel,
  ModalHeader,
  ModalTitle,
  ModalSubtitle,
  ModalContent,
  ModalFooter,
  FormGroup,
  FormLabel,
  FormTextarea,
  FormRow,
  SummaryBox,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  OrderSummaryBox,
  OrderSummaryGrid,
  OrderSummaryItem,
  OrderSummaryLabel,
  OrderSummaryValue,
  ProgressBar,
  ProgressFill,
  ProgressLabel,
  PaymentTimeline,
  PaymentTimelineItem,
  TimelineDate,
  TimelineId,
  TimelineMethod,
  TimelineAmount,
  TimelineStatus,
  TimelineAction,
  OrderActionBar,
  MethodFieldsContainer,
  FieldsSection,
  // STEP 1: Payment Impact
  ImpactBanner,
  // STEP 3: Why Payment Required
  WhyPaymentSection,
  WhyPaymentList,
  // STEP 4: Partial Payment Logic
  PaymentRulePanel,
  RuleAmounts,
  RuleAmountItem,
  RuleAmountLabel,
  RuleAmountValue,
  RuleStatus,
  RuleStatusList,
  // STEP 5: Payment Verification
  VerificationSection,
  VerificationGrid,
  VerificationItem,
  VerificationLabel,
  VerificationValue,
  // STEP 6: People Involved
  PeopleSection,
  PersonCard,
  PersonRole,
  PersonName,
  PersonDetails,
  // STEP 2: Payment Lifecycle
  PaymentLifecycleCard,
  LifecycleState,
  LifecycleTimeline,
  LifecycleStep,
  LifecycleStepTitle,
  LifecycleStepDetail,
  // STEP 4: Due Date & Aging
  DueDateCard,
  DueDateItem,
  DueDateLabel,
  DueDateValue,
  AgingBadge,
  // STEP 5: Payment Methods
  PaymentMethodsPanel,
  PaymentMethodButton,
  MethodBadge,
  // STEP 6: Downstream Blocks
  DownstreamBlocksCard,
  BlocksList,
} from './styles';
import { ContactPanel } from '../../../shared/components/ContactPanel/ContactPanel';
import { Contact } from '../../../shared/types/contact';
import { PaymentRailRecommendation } from './PaymentRailRecommendation';

const Select: React.FC<React.SelectHTMLAttributes<HTMLSelectElement>> = (props) => {
  const theme = useTheme();
  return (
    <select
      {...props}
      style={{
        width: '100%',
        minHeight: '40px',
        borderRadius: '10px',
        border: `1px solid ${theme.colors.border}`,
        padding: '0.5rem 0.75rem',
        background: theme.colors.surface,
        ...(props.style || {}),
      }}
    />
  );
};

const LayoutGridComponent: React.FC<{ children: React.ReactNode; mainRatio?: string; sideRatio?: string; gap?: string }> = ({ children, sideRatio, gap }) => (
  <div style={{ display: 'grid', gridTemplateColumns: `1fr ${sideRatio || '360px'}`, gap: gap || '20px' }}>{children}</div>
);

const LayoutMain: React.FC<{ children: React.ReactNode }> = ({ children }) => <div>{children}</div>;

const LayoutSide = React.forwardRef<HTMLDivElement, { children: React.ReactNode; $top?: number }>(
  ({ children, $top }, ref) => (
    <div ref={ref} style={{ position: 'sticky', top: `${$top || 120}px`, alignSelf: 'start' }}>
      {children}
    </div>
  )
);
LayoutSide.displayName = 'LayoutSide';

const CardContainer: React.FC<{ children: React.ReactNode; variant?: string; padding?: string; hoverable?: boolean }> = ({ children }) => {
  const theme = useTheme();
  return (
    <div style={{ border: `1px solid ${theme.colors.border}`, borderRadius: '14px', background: theme.colors.surface, padding: '1rem' }}>{children}</div>
  );
};

const PAYMENTS_DATA_QUERY = gql`
  query PaymentsData($limit: Int = 500, $offset: Int = 0) {
    orderPayments(limit: $limit, offset: $offset) {
      id
      orderId
      amount
      paymentStatus
      paymentMethod
      transactionReference
      createdAt
      currency
    }
    orders(limit: $limit, offset: $offset) {
      id
      orderNo
      finalAmount
      orderStatus
      currency
    }
    forexComplianceRecords(limit: $limit, offset: $offset) {
      id
      remittancePlan {
        tranches {
          percentage
          amount
          date
          milestone
        }
      }
    }
    orderShipments(limit: $limit, offset: $offset) {
      id
      orderId
      contacts {
        id
        name
        organization
        role
        team
        email
        phone
        visibility
        isPrimary
        assignmentRole
      }
    }
  }
`;

const CREATE_ORDER_PAYMENT_MUTATION = gql`
  mutation CreateOrderPayment($data: CreateOrderPaymentsInput!) {
    createOrderPayments(data: $data) {
      id
      orderId
      amount
      currency
      paymentStatus
      paymentMethod
      transactionReference
      createdAt
    }
  }
`;
const SEND_PAYMENT_REMINDER_MUTATION = gql`
  mutation SendPaymentReminder($input: SendPaymentReminderInput!) {
    sendPaymentReminder(input: $input) {
      success
      message
      orderId
      actionType
      timestamp
    }
  }
`;

const MARK_PAYMENT_DISPUTED_MUTATION = gql`
  mutation MarkPaymentDisputed($input: MarkPaymentDisputedInput!) {
    markPaymentDisputed(input: $input) {
      success
      message
      orderId
      actionType
      timestamp
    }
  }
`;

const ASSIGN_TO_COLLECTIONS_MUTATION = gql`
  mutation AssignToCollections($input: AssignToCollectionsInput!) {
    assignToCollections(input: $input) {
      success
      message
      orderId
      actionType
      timestamp
    }
  }
`;

const statusPriority: Record<Payment['paymentStatus'], number> = {
  overdue: 4,
  pending: 3,
  partial: 2,
  paid: 1,
};

// Color rules for outstanding amounts by status
const getOutstandingColor = (
  status: Payment['paymentStatus'],
  due: number,
  colors: { success: string; error: string; warning: string; gray500: string },
) => {
  if (due <= 0) return colors.success; // green for zero
  if (status === 'overdue') return colors.error; // red for overdue
  if (status === 'partial' || status === 'pending') return colors.warning; // amber for partial/pending
  return colors.gray500; // default muted
};

// Payment method field mapping
type PaymentMethod = 'Bank Transfer' | 'Credit Card' | 'Debit Card' | 'UPI' | 'Wallet' | 'Cash' | 'Check' | 'Wire Transfer' | 'System';

interface MethodFieldConfig {
  label: string;
  fields: {
    base: string[]; // Always visible
    conditional: Record<string, boolean>; // Method-specific
  };
}

const PAYMENT_METHOD_FIELDS: Record<PaymentMethod, MethodFieldConfig> = {
  'Bank Transfer': {
    label: 'Bank Transfer',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        bankName: true,
        transactionReference: true,
        transferDate: true,
        accountLast4: false, // optional
      },
    },
  },
  'Credit Card': {
    label: 'Credit Card',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        cardType: true,
        cardLast4: true,
        authorizationId: true,
      },
    },
  },
  'Debit Card': {
    label: 'Debit Card',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        cardType: true,
        cardLast4: true,
        authorizationId: true,
      },
    },
  },
  'Wire Transfer': {
    label: 'Wire Transfer',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        bankName: true,
        transactionReference: true,
        transferDate: true,
        accountLast4: false,
      },
    },
  },
  'UPI': {
    label: 'UPI',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        upiId: true,
        transactionId: true,
      },
    },
  },
  'Wallet': {
    label: 'Wallet',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        walletName: true,
        transactionId: true,
      },
    },
  },
  'Cash': {
    label: 'Cash',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        receivedBy: true,
        receiptNumber: true,
        receivedLocation: true,
      },
    },
  },
  'Check': {
    label: 'Check',
    fields: {
      base: ['amount', 'method', 'paidBy', 'paymentDate', 'notes'],
      conditional: {
        checkNumber: true,
        bankName: true,
        receivedBy: true,
      },
    },
  },
  'System': {
    label: 'Auto-settled',
    fields: {
      base: ['amount', 'paymentDate'], // Minimal fields
      conditional: {
        settlementBatchId: true,
        gatewayName: true,
      },
    },
  },
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

// STEP 1: Fix payment status calculation logic
// Status is DERIVED from amounts and dates, never manually set
const calculatePaymentStatus = (
  totalAmount: number,
  paidAmount: number,
  dueDate?: string
): Payment['paymentStatus'] => {
  const outstanding = totalAmount - paidAmount;
  
  // Rule 1: Outstanding = 0 → Paid
  if (outstanding === 0) return 'paid';
  
  // Rule 2: Paid > 0 and Outstanding > 0 → Partial
  if (paidAmount > 0 && outstanding > 0) return 'partial';
  
  // Rule 3 & 4: Paid = 0
  if (paidAmount === 0) {
    // Check if past due date
    if (dueDate) {
      const today = new Date();
      const due = new Date(dueDate);
      if (today > due) return 'overdue';
    }
    return 'pending';
  }
  
  return 'pending';
};

const formatDateOnly = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });

// Component for rendering method-specific fields
interface PaymentFormState {
  amount: string;
  method: string;
  paidBy: string;
  paymentDate: string;
  notes: string;
  bankName?: string;
  transactionReference?: string;
  transferDate?: string;
  accountLast4?: string;
  cardType?: string;
  cardLast4?: string;
  authorizationId?: string;
  upiId?: string;
  walletName?: string;
  transactionId?: string;
  receivedBy?: string;
  receiptNumber?: string;
  receivedLocation?: string;
  checkNumber?: string;
  settlementBatchId?: string;
  gatewayName?: string;
}

const MethodSpecificFields: React.FC<{
  method: string;
  formData: PaymentFormState;
  onFormChange: (data: Partial<PaymentFormState>) => void;
}> = ({ method, formData, onFormChange }) => {
  const theme = useTheme();
  const config = PAYMENT_METHOD_FIELDS[method as PaymentMethod];
  if (!config) return null;

  return (
    <MethodFieldsContainer>
      {/* Bank Transfer / Wire Transfer */}
      {(method === 'Bank Transfer' || method === 'Wire Transfer') && (
        <FieldsSection>
          <SectionHeading size="sm" icon="Building2">Bank Details</SectionHeading>
          <FormGroup>
            <FormLabel>Bank Name *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., HDFC Bank"
              value={formData.bankName || ''}
              onChange={(e) => onFormChange({ bankName: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Transaction Reference / UTR *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., UTR123456789"
              value={formData.transactionReference || ''}
              onChange={(e) => onFormChange({ transactionReference: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Transfer Date</FormLabel>
            <Input
              type="date"
              value={formData.transferDate || ''}
              onChange={(e) => onFormChange({ transferDate: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Account Last 4 Digits (Optional)</FormLabel>
            <Input
              type="text"
              placeholder="e.g., 1234"
              maxLength={4}
              value={formData.accountLast4 || ''}
              onChange={(e) => onFormChange({ accountLast4: e.target.value })}
            />
          </FormGroup>
        </FieldsSection>
      )}

      {/* Credit / Debit Card */}
      {(method === 'Credit Card' || method === 'Debit Card') && (
        <FieldsSection>
          <SectionHeading size="sm" icon="CreditCard">Card Details</SectionHeading>
          <FormGroup>
            <FormLabel>Card Type *</FormLabel>
            <Select
              value={formData.cardType || 'Visa'}
              onChange={(e) => onFormChange({ cardType: e.target.value })}
            >
              <option value="Visa">Visa</option>
              <option value="MasterCard">MasterCard</option>
              <option value="American Express">American Express</option>
              <option value="Rupay">RuPay</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <FormLabel>Last 4 Digits *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., 1234"
              maxLength={4}
              value={formData.cardLast4 || ''}
              onChange={(e) => onFormChange({ cardLast4: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Authorization / Transaction ID *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., TXN123456"
              value={formData.authorizationId || ''}
              onChange={(e) => onFormChange({ authorizationId: e.target.value })}
            />
          </FormGroup>
          <div style={{ fontSize: '11px', color: theme.colors.textMuted, fontStyle: 'italic', padding: '8px', backgroundColor: theme.colors.gray100, borderRadius: '4px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Icon name="ShieldCheck" size={12} />
            Full card number, CVV, and expiry are never stored
          </div>
        </FieldsSection>
      )}

      {/* UPI */}
      {method === 'UPI' && (
        <FieldsSection>
          <SectionHeading size="sm" icon="Zap">UPI Details</SectionHeading>
          <FormGroup>
            <FormLabel>UPI ID *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., user@bankname"
              value={formData.upiId || ''}
              onChange={(e) => onFormChange({ upiId: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Transaction ID *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., TXN123456789"
              value={formData.transactionId || ''}
              onChange={(e) => onFormChange({ transactionId: e.target.value })}
            />
          </FormGroup>
        </FieldsSection>
      )}

      {/* Wallet */}
      {method === 'Wallet' && (
        <FieldsSection>
          <SectionHeading size="sm" icon="DollarSign">Wallet Details</SectionHeading>
          <FormGroup>
            <FormLabel>Wallet Name *</FormLabel>
            <Select
              value={formData.walletName || 'Google Pay'}
              onChange={(e) => onFormChange({ walletName: e.target.value })}
            >
              <option value="Google Pay">Google Pay</option>
              <option value="Apple Pay">Apple Pay</option>
              <option value="PhonePe">PhonePe</option>
              <option value="Paytm">Paytm</option>
              <option value="Amazon Pay">Amazon Pay</option>
            </Select>
          </FormGroup>
          <FormGroup>
            <FormLabel>Transaction ID *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., TXN123456"
              value={formData.transactionId || ''}
              onChange={(e) => onFormChange({ transactionId: e.target.value })}
            />
          </FormGroup>
        </FieldsSection>
      )}

      {/* Cash / Check */}
      {(method === 'Cash' || method === 'Check') && (
        <FieldsSection>
          <SectionHeading size="sm" icon="DollarSign">{method} Details</SectionHeading>
          {method === 'Check' && (
            <FormGroup>
              <FormLabel>Check Number *</FormLabel>
              <Input
                type="text"
                placeholder="e.g., CHK123456"
                value={formData.checkNumber || ''}
                onChange={(e) => onFormChange({ checkNumber: e.target.value })}
              />
            </FormGroup>
          )}
          {method === 'Check' && (
            <FormGroup>
              <FormLabel>Bank Name *</FormLabel>
              <Input
                type="text"
                placeholder="e.g., HDFC Bank"
                value={formData.bankName || ''}
                onChange={(e) => onFormChange({ bankName: e.target.value })}
              />
            </FormGroup>
          )}
          <FormGroup>
            <FormLabel>Received By *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., John Smith"
              value={formData.receivedBy || ''}
              onChange={(e) => onFormChange({ receivedBy: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Receipt Number *</FormLabel>
            <Input
              type="text"
              placeholder="e.g., RCP2024001"
              value={formData.receiptNumber || ''}
              onChange={(e) => onFormChange({ receiptNumber: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Received Location</FormLabel>
            <Input
              type="text"
              placeholder="e.g., Head Office, Delhi"
              value={formData.receivedLocation || ''}
              onChange={(e) => onFormChange({ receivedLocation: e.target.value })}
            />
          </FormGroup>
        </FieldsSection>
      )}

      {/* System / Auto-settled */}
      {method === 'System' && (
        <FieldsSection>
          <SectionHeading size="sm" icon="Lock">Gateway Details (Read-only)</SectionHeading>
          <FormGroup>
            <FormLabel>Settlement Batch ID</FormLabel>
            <Input
              type="text"
              value={formData.settlementBatchId || ''}
              disabled
              onChange={(e) => onFormChange({ settlementBatchId: e.target.value })}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Gateway Name</FormLabel>
            <Input
              type="text"
              value={formData.gatewayName || ''}
              disabled
              onChange={(e) => onFormChange({ gatewayName: e.target.value })}
            />
          </FormGroup>
        </FieldsSection>
      )}
    </MethodFieldsContainer>
  );
};

interface OrderPaymentQueryItem {
  id?: string;
  [key: string]: unknown;
}

interface OrderQueryItem {
  id?: string;
  [key: string]: unknown;
}

interface ForexComplianceRecordQueryItem {
  id?: string;
  remittancePlan?: {
    tranches?: Array<{
      percentage?: number;
      amount?: number;
      date?: string;
      milestone?: string;
    }>;
  };
}

interface ShipmentContactQueryItem {
  id?: number;
  name?: string;
  organization?: string;
  role?: string;
  team?: string;
  email?: string;
  phone?: string;
  visibility?: string;
  isPrimary?: boolean;
  assignmentRole?: string;
}

interface OrderShipmentQueryItem {
  id?: number;
  orderId?: number;
  contacts?: ShipmentContactQueryItem[];
}

interface PaymentsDataQueryResult {
  orderPayments: OrderPaymentQueryItem[];
  orders: OrderQueryItem[];
  forexComplianceRecords: ForexComplianceRecordQueryItem[];
  orderShipments: OrderShipmentQueryItem[];
}

const getCanonicalOrderId = (orderNo: unknown, orderId: number): string => {
  const normalized = typeof orderNo === 'string' ? orderNo.trim() : '';
  if (normalized) return normalized;
  return `ORD-${orderId}`;
};

const buildOrderMilestones = (order: {
  orderId: string;
  total: number;
  dueDate?: string;
  remittanceTranches?: Array<{
    percentage?: number;
    amount?: number;
    dueDate?: string;
    milestone?: string;
  }>;
}) => {
  const orderDate = order.dueDate ? new Date(order.dueDate) : new Date();
  if (!order.remittanceTranches || order.remittanceTranches.length === 0) {
    return [];
  }

  const invoiceTag = `INV-${order.orderId.replace('ORD-', '')}`;
  let runningThreshold = 0;
  return order.remittanceTranches.map((tranche, idx) => {
    const trancheAmount = tranche.amount || order.total * ((tranche.percentage || 0) / 100);
    runningThreshold += trancheAmount;
    return {
      label: tranche.milestone || `Milestone ${idx + 1}`,
      percentage: tranche.percentage || 0,
      threshold: runningThreshold,
      dueDate: tranche.dueDate || orderDate.toISOString(),
      dueDateLabel: tranche.dueDate || 'TBD',
      invoice: invoiceTag,
    };
  });
};

export const Payments: React.FC<{
  onOrderSelect?: (orderId: string) => void;
  initialOrderId?: string;
  autoOpenPay?: boolean;
  onAutoOpenPayHandled?: () => void;
  onPaymentSuccess?: (orderId: string, message: string) => void;
  onOpenMessaging?: (payload: { orderId: string; contactName?: string; contactEmail?: string }) => void;
}> = ({ onOrderSelect, initialOrderId, autoOpenPay, onAutoOpenPayHandled, onPaymentSuccess, onOpenMessaging }) => {
  const { role, can } = useRole();
  const theme = useTheme();
  const { formatCurrency } = useCurrencyFormatter();
  const { data: paymentsData, refetch: refetchPaymentsData } = useGraphQLQuery<PaymentsDataQueryResult>(PAYMENTS_DATA_QUERY, {
    variables: { limit: 500, offset: 0 },
    showErrorToast: true,
  });
  const [createOrderPayment] = useGraphQLMutation(CREATE_ORDER_PAYMENT_MUTATION, {
    showErrorToast: true,
    showSuccessToast: false,
  });
    const [sendPaymentReminder] = useGraphQLMutation(SEND_PAYMENT_REMINDER_MUTATION, {
      showErrorToast: true,
      showSuccessToast: false,
    });
    const [markPaymentDisputed] = useGraphQLMutation(MARK_PAYMENT_DISPUTED_MUTATION, {
      showErrorToast: true,
      showSuccessToast: false,
    });
    const [assignToCollections] = useGraphQLMutation(ASSIGN_TO_COLLECTIONS_MUTATION, {
      showErrorToast: true,
      showSuccessToast: false,
    });
  const [selectedOrderId, setSelectedOrderId] = useState<string>(initialOrderId || 'ALL');
  const [activePaymentId, setActivePaymentId] = useState<string | null>(null);
  const [payNowPaymentId, setPayNowPaymentId] = useState<string | null>(null);
  const [showNewPayment, setShowNewPayment] = useState(false);
  const [newPaymentOrderId, setNewPaymentOrderId] = useState<string>('');
  const [isSubmittingPayment, setIsSubmittingPayment] = useState(false);

  // Permission checks
  const canViewPayments = can('payments.view');
  const canPayInvoices = can('payments.pay');

  const paymentContacts = useMemo<{ financeOwner?: Contact; billingContact?: Contact }>(() => {
    if (selectedOrderId === 'ALL') {
      return {};
    }

    const selectedOrder = (paymentsData?.orders || []).find((order) => {
      const backendId = Number(order.id || 0);
      return getCanonicalOrderId(order.orderNo, backendId) === selectedOrderId;
    });

    const backendOrderId = Number(selectedOrder?.id || 0);
    if (!backendOrderId) {
      return {};
    }

    const shipmentForOrder = (paymentsData?.orderShipments || []).find((shipment) => Number(shipment.orderId || 0) === backendOrderId);
    const contacts = shipmentForOrder?.contacts || [];

    const mapRole = (roleValue?: string): Contact['role'] => {
      const normalized = String(roleValue || '').toLowerCase();
      if (normalized === 'customer') return 'Customer';
      if (normalized === 'manufacturer') return 'Manufacturer';
      if (normalized === 'ops owner' || normalized === 'ops_owner') return 'Ops Owner';
      if (normalized === 'carrier') return 'Carrier';
      if (normalized === 'customs broker' || normalized === 'customs_broker') return 'Customs Broker';
      if (normalized === 'compliance officer' || normalized === 'compliance_officer') return 'Compliance Officer';
      if (normalized === 'finance contact' || normalized === 'finance_contact') return 'Finance Contact';
      if (normalized === 'authority') return 'Authority';
      return 'Finance Contact';
    };

    const toContact = (contact?: ShipmentContactQueryItem): Contact | undefined => {
      if (!contact?.id || !contact?.name || !contact?.email) {
        return undefined;
      }
      return {
        id: String(contact.id),
        name: contact.name,
        organization: contact.organization || undefined,
        role: mapRole(contact.role),
        email: contact.email,
        phone: contact.phone || undefined,
        visibility: contact.visibility === 'Customer-visible' ? 'Customer-visible' : 'Internal',
        primary: Boolean(contact.isPrimary),
        linkedOrders: [selectedOrderId],
      };
    };

    const financeOwnerRaw = contacts.find((contact) => {
      const roleValue = String(contact.role || '').toLowerCase();
      const assignment = String(contact.assignmentRole || '').toLowerCase();
      const team = String(contact.team || '').toLowerCase();
      return roleValue.includes('finance') || assignment.includes('finance') || team.includes('finance');
    });

    const billingContactRaw = contacts.find((contact) => {
      const roleValue = String(contact.role || '').toLowerCase();
      const assignment = String(contact.assignmentRole || '').toLowerCase();
      const visibility = String(contact.visibility || '').toLowerCase();
      return roleValue.includes('customer') || assignment.includes('billing') || visibility.includes('customer-visible');
    });

    return {
      financeOwner: toContact(financeOwnerRaw),
      billingContact: toContact(billingContactRaw),
    };
  }, [paymentsData?.orderShipments, paymentsData?.orders, selectedOrderId]);

  const peopleCount = [paymentContacts.financeOwner, paymentContacts.billingContact].filter(Boolean).length;

  // Build visible contacts (finance + admin see all; customers see limited)
  const visibleContacts = useMemo(() => {
    const internal: Contact[] = [];
    const external: Contact[] = [];

    if (['admin', 'finance', 'agent', 'ops'].includes(role) && paymentContacts.financeOwner) {
      internal.push(paymentContacts.financeOwner);
    }

    if (['admin', 'finance', 'agent', 'ops'].includes(role) && paymentContacts.billingContact) {
      external.push(paymentContacts.billingContact);
    }

    return { internal, external };
  }, [paymentContacts.billingContact, paymentContacts.financeOwner, role]);

  // Update selected order if initialOrderId changes
  useEffect(() => {
    if (initialOrderId) {
      setSelectedOrderId(initialOrderId);
    }
  }, [initialOrderId]);
  const [newPaymentForm, setNewPaymentForm] = useState<PaymentFormState>({
    amount: '',
    method: 'Bank Transfer',
    paidBy: 'Customer',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [payNowForm, setPayNowForm] = useState<PaymentFormState>({
    amount: '',
    method: 'Bank Transfer',
    paidBy: 'Customer',
    paymentDate: new Date().toISOString().split('T')[0],
    notes: '',
  });

  const headerRef = useRef<HTMLDivElement>(null);
  const railRef = useRef<HTMLDivElement>(null);
  const [railTop, setRailTop] = useState<number>(120);
  useLayoutEffect(() => {
    const updateTop = () => {
      const h = headerRef.current?.getBoundingClientRect().bottom || 0;
      setRailTop(h + 12);
    };
    updateTop();
    window.addEventListener('resize', updateTop);
    return () => window.removeEventListener('resize', updateTop);
  }, []);

  const paymentRecords = useMemo<Payment[]>(() => {
    const liveOrderPayments = paymentsData?.orderPayments || [];
    const liveOrders = paymentsData?.orders || [];

    if (!liveOrders.length && !liveOrderPayments.length) {
      return [];
    }

    const ORDER_STATUS_CODES = {
      IN_TRANSIT_PICKED_UP: 3,
      IN_TRANSIT_SHIPPED: 4,
      OUT_FOR_DELIVERY: 5,
      DELIVERED: 7,
    } as const;

    const ORDER_STATUS_LABELS: Record<number, string> = {
      [ORDER_STATUS_CODES.IN_TRANSIT_PICKED_UP]: 'In Transit',
      [ORDER_STATUS_CODES.IN_TRANSIT_SHIPPED]: 'In Transit',
      [ORDER_STATUS_CODES.OUT_FOR_DELIVERY]: 'Out for Delivery',
      [ORDER_STATUS_CODES.DELIVERED]: 'Delivered',
    };

    const mapOrderStatus = (statusCode: number | undefined): string => {
      if (statusCode === undefined) {
        return 'Processing';
      }

      return ORDER_STATUS_LABELS[statusCode] || 'Processing';
    };

    const orderMap = new Map<number, OrderQueryItem>();
    liveOrders.forEach((order) => {
      orderMap.set(Number(order.id || 0), order as unknown as OrderQueryItem);
    });

    const totalsByOrder = new Map<number, number>();
    liveOrderPayments.forEach((p) => {
      // paymentStatus is the raw backend code (0=pending, 1=paid, 2=failed,
      // 3=refunded) — only a status-1 row has actually settled money, so
      // summing every row here (regardless of status) previously made a
      // still-pending payment attempt count as paid.
      if (Number(p.paymentStatus) !== 1) return;
      const orderId = Number(p.orderId || 0);
      const curr = totalsByOrder.get(orderId) || 0;
      totalsByOrder.set(orderId, curr + Number(p.amount || 0));
    });

    if (!liveOrderPayments.length) {
      return liveOrders.map((order) => {
        const orderIdNum = Number(order.id || 0);
        const totalOrderAmount = Number(order.finalAmount || 0);
        const canonicalOrderId = getCanonicalOrderId(order.orderNo, orderIdNum);

        return {
          id: `PAY-PENDING-${orderIdNum}`,
          orderId: canonicalOrderId,
          orderStatus: mapOrderStatus(Number(order.orderStatus)),
          customerId: `CUST-${orderIdNum}`,
          customerName: `Order ${canonicalOrderId}`,
          currency: order.currency || 'INR',
          totalOrderAmount,
          paymentAmount: 0,
          dueAmount: totalOrderAmount,
          paymentStatus: calculatePaymentStatus(totalOrderAmount, 0) as Payment['paymentStatus'],
          paymentMethod: 'System',
          paidBy: 'System',
          initiatedAt: new Date().toISOString(),
          initiatedBy: 'System',
          processedAt: new Date().toISOString(),
          processedBy: 'System',
          referenceId: undefined,
          notes: 'No payment records yet',
        } as Payment;
      });
    }

    return liveOrderPayments.map((p) => {
      const orderIdNum = Number(p.orderId || 0);
      const order = orderMap.get(orderIdNum);
      const totalPaidForOrder = totalsByOrder.get(orderIdNum) || 0;
      // If orders field is temporarily unavailable, avoid showing zero order value.
      const totalOrderAmount = Number(order?.finalAmount || totalPaidForOrder || Number(p.amount || 0));
      const dueAmount = Math.max(totalOrderAmount - totalPaidForOrder, 0);
      const paymentAmount = Number(p.amount || 0);
      const canonicalOrderId = getCanonicalOrderId(order?.orderNo, orderIdNum);

      return {
        id: `PAY-${p.id}`,
        orderId: canonicalOrderId,
        orderStatus: mapOrderStatus(Number(order?.orderStatus)),
        customerId: `CUST-${orderIdNum}`,
        customerName: `Order ${canonicalOrderId}`,
        currency: order?.currency || p.currency || 'INR',
        totalOrderAmount,
        paymentAmount,
        dueAmount,
        paymentStatus: calculatePaymentStatus(totalOrderAmount, totalPaidForOrder) as Payment['paymentStatus'],
        paymentMethod: p.paymentMethod || 'System',
        paidBy: 'Customer',
        initiatedAt: p.createdAt || new Date().toISOString(),
        initiatedBy: 'System',
        processedAt: p.createdAt || new Date().toISOString(),
        processedBy: 'System',
        referenceId: p.transactionReference || `REF-${p.id}`,
        notes: 'Loaded from backend orderPayments',
      } as Payment;
    });
  }, [paymentsData]);

  const backendOrderLookup = useMemo(() => {
    const lookup = new Map<string, { backendOrderId: number; currency: string }>();

    (paymentsData?.orders || []).forEach((order) => {
      const backendOrderId = Number(order.id || 0);
      if (!backendOrderId) return;
      const canonicalOrderId = getCanonicalOrderId(order.orderNo, backendOrderId);
      lookup.set(canonicalOrderId, {
        backendOrderId,
        currency: String(order.currency || 'INR'),
      });
    });
    return lookup;
  }, [paymentsData?.orders]);

  const getPaymentGatewayCode = (method: string): number => {
    const normalized = String(method || '').toLowerCase();
    if (normalized.includes('card') || normalized === 'upi' || normalized === 'wallet') {
      return 1;
    }
    if (normalized.includes('wire')) {
      return 2;
    }
    return 3;
  };

  const getPaymentReference = (form: PaymentFormState): string | undefined => {
    const reference =
      form.transactionReference ||
      form.transactionId ||
      form.authorizationId ||
      form.upiId ||
      form.receiptNumber ||
      form.checkNumber;
    return reference ? String(reference).trim() : undefined;
  };

  const persistPayment = async (
    canonicalOrderId: string,
    form: PaymentFormState,
  ): Promise<number> => {
    const amount = Number(form.amount || 0);
    if (!(amount > 0)) {
      throw new Error('Enter a valid payment amount');
    }

    const backendOrder = backendOrderLookup.get(canonicalOrderId);
    if (!backendOrder) {
      throw new Error(`Unable to resolve backend order for ${canonicalOrderId}`);
    }

    const currency = backendOrder.currency || 'INR';
    await createOrderPayment({
      variables: {
        data: {
          orderId: backendOrder.backendOrderId,
          paymentType: 1,
          amount,
          currency,
          conversionRate: 1,
          amountBase: amount,
          paymentGateway: getPaymentGatewayCode(form.method),
          paymentStatus: 1,
          paymentMethod: form.method,
          transactionReference: getPaymentReference(form),
          paymentGatewayResponse: form.notes || null,
        },
      },
    });

    await refetchPaymentsData();
    return amount;
  };

  const orders = useMemo(() => {
    const map = new Map<string, {
      orderId: string;
      customerName: string;
      total: number;
      due: number;
      paid: number;
      status: Payment['paymentStatus'];
      currency: string;
      dueDate?: string;
      blocked?: boolean;
      blockedReason?: string;
      remittanceTranches?: Array<{
        percentage?: number;
        amount?: number;
        dueDate?: string;
        milestone?: string;
      }>;
    }>();

    // Build lookup of remittance tranches from forex compliance records
    const tranchesLookup = new Map<string, Array<{ percentage?: number; amount?: number; dueDate?: string; milestone?: string }>>();
    if (paymentsData?.forexComplianceRecords) {
      paymentsData.forexComplianceRecords.forEach((record) => {
        const canonicalId = String(record.id || '').trim();
        const tranches = (record.remittancePlan?.tranches || []).map((tranche) => ({
          percentage: tranche.percentage,
          amount: tranche.amount,
          dueDate: tranche.date,
          milestone: tranche.milestone,
        }));

        if (canonicalId && tranches.length > 0) {
          tranchesLookup.set(canonicalId, tranches);
        }
      });
    }

    // STEP 1 & 2: Aggregate payments per order and calculate correct status
    // totalOrderAmount/dueAmount/paymentStatus are already computed correctly
    // per record above (from totalsByOrder, which only counts settled rows) —
    // every record for the same order carries the same values, so reuse them
    // directly instead of re-summing paymentAmount (which is the raw attempt
    // amount and doesn't reflect whether that attempt actually settled).
    paymentRecords.forEach((p) => {
      const existing = map.get(p.orderId);
      const paidSoFar = p.totalOrderAmount - p.dueAmount;

      if (!existing) {
        map.set(p.orderId, {
          orderId: p.orderId,
          customerName: p.customerName,
          total: p.totalOrderAmount,
          due: p.dueAmount,
          paid: paidSoFar,
          status: p.paymentStatus,
          currency: p.currency,
          dueDate: p.dueDate,
          blocked: Boolean(p.blockingImpact?.isBlocked || (p.paymentImpact?.blockedBy && p.paymentImpact.blockedBy.length > 0)),
          blockedReason: p.blockingImpact?.blockedOperations?.join(', ') || (p.paymentImpact?.blockedBy || []).join(', '),
          remittanceTranches: tranchesLookup.get(p.orderId),
        });
      } else {
        map.set(p.orderId, {
          ...existing,
          due: p.dueAmount,
          paid: paidSoFar,
          status: p.paymentStatus,
          dueDate: existing.dueDate || p.dueDate,
          blocked: existing.blocked || Boolean(p.blockingImpact?.isBlocked || (p.paymentImpact?.blockedBy && p.paymentImpact.blockedBy.length > 0)),
          blockedReason: existing.blockedReason || p.blockingImpact?.blockedOperations?.join(', ') || (p.paymentImpact?.blockedBy || []).join(', '),
          remittanceTranches: existing.remittanceTranches || tranchesLookup.get(p.orderId),
        });
      }
    });

    return Array.from(map.values());
  }, [paymentRecords, paymentsData?.forexComplianceRecords]);

  const filteredPayments = useMemo(() => {
    if (selectedOrderId === 'ALL') return paymentRecords;
    return paymentRecords.filter((p) => p.orderId === selectedOrderId);
  }, [selectedOrderId, paymentRecords]);

  const selectedOrderOption = useMemo(() => {
    if (selectedOrderId === 'ALL') {
      return 'All Orders';
    }

    const selectedOrder = orders.find((order) => order.orderId === selectedOrderId);
    return selectedOrder ? `${selectedOrder.orderId} — ${selectedOrder.customerName}` : selectedOrderId;
  }, [orders, selectedOrderId]);

  const currentOrder = selectedOrderId === 'ALL'
    ? undefined
    : orders.find((o) => o.orderId === selectedOrderId);

  // Coming from the dashboard's "Pay now" should land straight on the pay
  // form instead of making the customer click "Pay" again once the order
  // resolves — currentOrder isn't available until this order's payment rows
  // have loaded, so this can't just fire on click.
  useEffect(() => {
    if (!autoOpenPay || !currentOrder) return;
    if (currentOrder.due > 0) {
      setPayNowPaymentId('ORDER_LEVEL');
    }
    onAutoOpenPayHandled?.();
  }, [autoOpenPay, currentOrder, onAutoOpenPayHandled]);

  const activePayment = activePaymentId
    ? filteredPayments.find((p) => p.id === activePaymentId) || null
    : null;

  // STEP 2: Payment counting - align header totals with order records
  const paymentStats = useMemo(() => {
    // When a specific order is selected, base stats on that order's aggregated status
    if (selectedOrderId !== 'ALL' && currentOrder) {
      const orderPayments = filteredPayments;
      // Reuse the order's already-correct derived status (from the orders
      // aggregation above) instead of re-summing paymentAmount here too —
      // that raw sum doesn't reflect whether each attempt actually settled.
      const derivedStatus = currentOrder.status;

      return {
        total: orderPayments.length,
        paid: derivedStatus === 'paid' ? 1 : 0,
        pending: (derivedStatus === 'pending' || derivedStatus === 'partial') ? orderPayments.length : 0,
        overdue: derivedStatus === 'overdue' ? 1 : 0,
      };
    }
    
    // When viewing ALL, align with the rows shown (orders aggregation, not raw transactions)
    const stats = {
      total: orders.length,
      paid: 0,
      pending: 0,
      overdue: 0,
    };
    orders.forEach((o) => {
      if (o.status === 'paid') stats.paid += 1;
      else if (o.status === 'pending' || o.status === 'partial') stats.pending += 1;
      else if (o.status === 'overdue') stats.overdue += 1;
    });
    return stats;
  }, [filteredPayments, selectedOrderId, currentOrder, orders]);

  // Check if any payment is overdue (for subtle People panel accent)
  const hasOverduePayments = paymentStats.overdue > 0;

  const statusLabel = (status: Payment['paymentStatus']) => status.charAt(0).toUpperCase() + status.slice(1);

  const orderValueLabel = currentOrder
    ? formatCurrency(currentOrder.total)
    : 'All orders';

  const orderPaymentStatus = currentOrder
    ? currentOrder.status === 'overdue'
      ? 'Blocked (due to payment)'
      : currentOrder.status === 'paid'
        ? 'Released'
        : 'In progress'
    : 'All statuses';

  const currentOrderMilestones = useMemo(() => {
    if (!currentOrder) return [];
    return buildOrderMilestones(currentOrder);
  }, [currentOrder]);

  const nextDueMilestone = useMemo(() => {
    if (!currentOrder) return undefined;
    const paidAmount = currentOrder.total - currentOrder.due;
    return currentOrderMilestones.find((milestone) => paidAmount < milestone.threshold);
  }, [currentOrder, currentOrderMilestones]);

  useEffect(() => {
    setActivePaymentId(null);
    setPayNowPaymentId(null);
  }, [selectedOrderId]);

  return (
    <PaymentsContainer>
      <div ref={headerRef}>
        <PageHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <H1 style={{ margin: '0' }}>Payments</H1>
              {selectedOrderId !== 'ALL' && currentOrder && (
                <StatusBadge variant={currentOrder.status as 'paid' | 'partial' | 'pending' | 'overdue'} label={statusLabel(currentOrder.status)} />
              )}
            </div>
            <Subtitle>Manage and track all your payment transactions</Subtitle>
            <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
              <Icon name="Users" size={13} color={theme.colors.textSecondary} />
              <span style={{ fontSize: 13, color: theme.colors.textSecondary }}>People involved: {peopleCount}</span>
              <Button variant="ghost" size="sm" onClick={() => {
                if (railRef.current) {
                  railRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
                  railRef.current.focus({ preventScroll: true });
                }
              }} style={{ marginLeft: 8 }}>
                View people →
              </Button>
            </div>
          </div>
          <Button
            onClick={() => setShowNewPayment(true)}
            disabled={!canPayInvoices}
            title={!canPayInvoices ? 'Only permitted roles can record payments' : ''}
          >
            <Icon name="CreditCard" size={16} style={{ marginRight: 6 }} />
            Record Payment
          </Button>
        </PageHeader>
      </div>

      {canViewPayments && !canPayInvoices && (
        <div style={{
          background: theme.colors.warningLight,
          border: `1px solid ${theme.colors.warning}`,
          borderRadius: '8px',
          padding: '12px 16px',
          margin: '0 0 16px',
          display: 'flex',
          gap: '8px',
          alignItems: 'flex-start'
        }}>
          <Icon name="Lock" size={16} color={theme.colors.warningDark} />
          <div>
            <div style={{ fontWeight: 600, color: theme.colors.warningDark, marginBottom: '4px' }}>Read-Only Access</div>
            <div style={{ fontSize: '14px', color: theme.colors.warningDark }}>Only Finance and Admin can process payments. You have view-only access.</div>
          </div>
        </div>
      )}

      <OrderBar>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, maxWidth: '400px' }}>
          <SearchableSelect
            options={['All Orders', ...orders.map((order) => `${order.orderId} — ${order.customerName}`)]}
            value={selectedOrderOption}
            onChange={(value) => {
              const orderId = value === 'All Orders' ? 'ALL' : value.split(' — ')[0];
              setSelectedOrderId(orderId);
              if (orderId !== 'ALL' && onOrderSelect) {
                onOrderSelect(orderId);
              }
            }}
            placeholder="Search orders by ID or customer..."
          />
        </div>

        <OrderMeta>
          <OrderMetaItem>
            <OrderMetaLabel>Fulfilment Status</OrderMetaLabel>
            <StatusBadge variant={currentOrder?.status as 'paid' | 'partial' | 'pending' | 'overdue' || 'paid'} label={orderPaymentStatus} />
          </OrderMetaItem>
          <OrderMetaItem>
            <OrderMetaLabel>Customer</OrderMetaLabel>
            <OrderMetaValue>
              {currentOrder?.customerName || 'Multiple customers'}
            </OrderMetaValue>
          </OrderMetaItem>
          <OrderMetaItem>
            <OrderMetaLabel>Total Order Value</OrderMetaLabel>
            <OrderMetaValue>{orderValueLabel}</OrderMetaValue>
          </OrderMetaItem>
          <OrderMetaItem>
            <OrderMetaLabel>Payment Status</OrderMetaLabel>
            <OrderMetaValue>{orderPaymentStatus}</OrderMetaValue>
          </OrderMetaItem>
        </OrderMeta>
      </OrderBar>

      <LayoutGridComponent mainRatio="calc(100% - 380px)" sideRatio="360px" gap="20px">
        <LayoutMain>
          <div style={{
            display: 'flex',
            gap: 28,
            padding: '14px 16px',
            backgroundColor: theme.colors.gray100,
            borderRadius: '8px',
          }}>
            {[
              { label: 'Total', value: paymentStats.total, color: theme.colors.textPrimary },
              { label: 'Paid', value: paymentStats.paid, color: theme.colors.success },
              { label: 'Pending', value: paymentStats.pending, color: theme.colors.warning },
              { label: 'Overdue', value: paymentStats.overdue, color: theme.colors.error },
            ].map((stat) => (
              <div key={stat.label} style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ fontSize: '1.5rem', fontWeight: 800, color: stat.color, lineHeight: 1 }}>{stat.value}</span>
                <span style={{ fontSize: '12px', color: theme.colors.textSecondary, fontWeight: 500 }}>{stat.label}</span>
              </div>
            ))}
          </div>

          {/* Cross-border payment rail recommendation — helps an exporter pick the
              cheapest USD collection method (Karbon Forex, Skydo, Razorpay Export
              Account, or a traditional bank wire), accounting for the RBI PA-CB cap. */}
          <PaymentRailRecommendation
            key={selectedOrderId}
            initialValueUsd={currentOrder && currentOrder.currency === 'USD' ? (currentOrder.due > 0 ? currentOrder.due : currentOrder.total) : undefined}
            contextLabel={currentOrder ? currentOrder.orderId : undefined}
          />

          {/* Order-level summary (when order is selected) */}
      {selectedOrderId !== 'ALL' && currentOrder && (
        <OrderSummaryBox key={`summary-${selectedOrderId}`}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '16px', fontWeight: 600, color: theme.colors.gray800 }}>
                Order {currentOrder.orderId}
              </h3>
              <p style={{ margin: 0, fontSize: '13px', color: theme.colors.textSecondary }}>
                {currentOrder.customerName}
              </p>
            </div>
          </div>

          <OrderSummaryGrid>
            <OrderSummaryItem>
              <OrderSummaryLabel>Total Order Value</OrderSummaryLabel>
              <OrderSummaryValue>{formatCurrency(currentOrder.total, currentOrder.currency)}</OrderSummaryValue>
            </OrderSummaryItem>
            <OrderSummaryItem>
              <OrderSummaryLabel>Total Paid</OrderSummaryLabel>
              <OrderSummaryValue>{formatCurrency(currentOrder.total - currentOrder.due, currentOrder.currency)}</OrderSummaryValue>
            </OrderSummaryItem>
            <OrderSummaryItem>
              <OrderSummaryLabel>Outstanding</OrderSummaryLabel>
              <OrderSummaryValue style={{ color: getOutstandingColor(currentOrder.status, currentOrder.due, theme.colors) }}>
                {formatCurrency(currentOrder.due, currentOrder.currency)}
              </OrderSummaryValue>
            </OrderSummaryItem>
            <OrderSummaryItem>
              <OrderSummaryLabel>Payment Status</OrderSummaryLabel>
              <OrderSummaryValue>
                <StatusBadge variant={currentOrder.status as 'paid' | 'partial' | 'pending' | 'overdue'} label={currentOrder.status.charAt(0).toUpperCase() + currentOrder.status.slice(1)} />
                {/* ISSUE 5 FIX: Payment lifecycle indicator */}
                <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                  {currentOrder.status === 'pending' && (
                    <span>
                      Invoice issued • <span style={{ fontWeight: 500 }}>INV-{currentOrder.orderId.replace('ORD-', '')}</span> • {currentOrder.dueDate ? new Date(new Date(currentOrder.dueDate).getTime() - 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Jan 18, 2024'} → <a href="#" onClick={(e) => { e.preventDefault(); setSelectedOrderId(currentOrder.orderId); onOrderSelect?.(currentOrder.orderId); }} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>View</a>
                      <div style={{ marginTop: 2, color: theme.colors.success }}>Covers: {nextDueMilestone ? `${nextDueMilestone.label} (${nextDueMilestone.percentage}%)` : 'Current milestone'}</div>
                    </span>
                  )}
                  {currentOrder.status === 'partial' && 'Partially settled'}
                  {currentOrder.status === 'paid' && 'Fully settled'}
                  {currentOrder.status === 'overdue' && (
                    <span>
                      Invoice issued • <span style={{ fontWeight: 500 }}>INV-{currentOrder.orderId.replace('ORD-', '')}</span> • {currentOrder.dueDate ? new Date(new Date(currentOrder.dueDate).getTime() - 30 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : 'Jan 18, 2024'} → <a href="#" onClick={(e) => { e.preventDefault(); setSelectedOrderId(currentOrder.orderId); onOrderSelect?.(currentOrder.orderId); }} style={{ color: theme.colors.primary, textDecoration: 'underline' }}>View</a>
                      <div style={{ marginTop: 2, color: theme.colors.error }}>Covers: {nextDueMilestone ? `${nextDueMilestone.label} (${nextDueMilestone.percentage}%)` : 'Current milestone'}</div>
                    </span>
                  )}
                </div>
              </OrderSummaryValue>
            </OrderSummaryItem>
            
            {/* STEP 3: Due Date Visibility - ISSUE 1 FIX: Hide overdue when outstanding=0 */}
            {currentOrder.dueDate && currentOrder.due > 0 && (() => {
              const paidAmount = currentOrder.total - currentOrder.due;
              const overdueMS = currentOrderMilestones.find((m) => paidAmount < m.threshold && new Date() > new Date(m.dueDate));
              
              return (
                <OrderSummaryItem>
                  <OrderSummaryLabel>Payment Due Date</OrderSummaryLabel>
                  <OrderSummaryValue>
                    {new Date(currentOrder.dueDate).toLocaleDateString()}
                    {overdueMS && (
                      <div style={{ fontSize: '11px', color: theme.colors.error, marginTop: '2px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="AlertTriangle" size={11} />
                        Overdue due to {overdueMS.label} ({overdueMS.percentage}%) — due {new Date(overdueMS.dueDate).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                      </div>
                    )}
                  </OrderSummaryValue>
                </OrderSummaryItem>
              );
            })()}
            
            {/* ISSUE 1 FIX: Show paid date when outstanding=0 */}
            {currentOrder.due === 0 && filteredPayments.length > 0 && (() => {
              const lastPayment = [...filteredPayments].sort(
                (a, b) => new Date(b.processedAt).getTime() - new Date(a.processedAt).getTime()
              )[0];
              const paidDate = new Date(lastPayment.processedAt);
              const wasLate = currentOrder.dueDate && paidDate > new Date(currentOrder.dueDate);
              
              return (
                <OrderSummaryItem>
                  <OrderSummaryLabel>Paid On</OrderSummaryLabel>
                  <OrderSummaryValue>
                    {paidDate.toLocaleDateString()}
                    {wasLate && (
                      <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '2px' }}>
                        Paid after due date
                      </div>
                    )}
                  </OrderSummaryValue>
                </OrderSummaryItem>
              );
            })()}
          </OrderSummaryGrid>

          {/* Next Actions for Overdue */}
          {currentOrder.status === 'overdue' && currentOrder.due > 0 && (
            <div style={{ marginTop: 12, padding: '12px', background: theme.colors.errorLight, borderRadius: 8, border: `1px solid ${theme.colors.error}` }}>
              <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.errorDark, marginBottom: 10, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="AlertTriangle" size={13} />
                Next Actions (Overdue)
              </div>
              <div style={{ display: 'grid', gap: 8 }}>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await sendPaymentReminder({
                          variables: {
                            input: {
                              orderId: currentOrder.orderId,
                              recipient: 'customer',
                              message: `Payment reminder for ${currentOrder.orderId}`,
                              actor: 'Finance UI',
                            },
                          },
                        });
                        await refetchPaymentsData();
                      } catch {
                        // Error toast is handled by useGraphQLMutation.
                      }
                    }}
                    style={{ justifyContent: 'flex-start', fontSize: '13px', color: theme.colors.gray700, width: '100%', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon name="Mail" size={13} />
                    Send reminder
                  </Button>
                  <div style={{ fontSize: '10px', color: theme.colors.textSecondary, marginTop: '3px', paddingLeft: '4px' }}>Email billing contact & log activity</div>
                </div>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        await markPaymentDisputed({
                          variables: {
                            input: {
                              orderId: currentOrder.orderId,
                              disputeReason: `Dispute raised from overdue workflow for ${currentOrder.orderId}`,
                              amount: currentOrder.due,
                              actor: 'Finance UI',
                            },
                          },
                        });
                        await refetchPaymentsData();
                      } catch {
                        // Error toast is handled by useGraphQLMutation.
                      }
                    }}
                    style={{ justifyContent: 'flex-start', fontSize: '13px', color: theme.colors.gray700, width: '100%', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon name="AlertCircle" size={13} />
                    Mark disputed
                  </Button>
                  <div style={{ fontSize: '10px', color: theme.colors.textSecondary, marginTop: '3px', paddingLeft: '4px' }}>Pauses automated reminders</div>
                </div>
                <div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      if (!confirm('Assign to collections? This will:\n• Lock shipment\n• Change order to Collections status\n• Escalate internally')) {
                        return;
                      }
                      try {
                        await assignToCollections({
                          variables: {
                            input: {
                              orderId: currentOrder.orderId,
                              reason: `Overdue payment escalation for ${currentOrder.orderId}`,
                              actor: 'Finance UI',
                            },
                          },
                        });
                        await refetchPaymentsData();
                      } catch {
                        // Error toast is handled by useGraphQLMutation.
                      }
                    }}
                    style={{ justifyContent: 'flex-start', fontSize: '13px', color: theme.colors.gray700, width: '100%', display: 'flex', alignItems: 'center', gap: 6 }}
                  >
                    <Icon name="Clipboard" size={13} />
                    Assign to collections
                  </Button>
                  <div style={{ fontSize: '10px', color: theme.colors.error, marginTop: '3px', paddingLeft: '4px', fontWeight: 500 }}>Locks shipment & escalates internally</div>
                </div>
              </div>
            </div>
          )}

          {/* Payment Structure / Milestones */}
          <div style={{ marginTop: 12, padding: '12px', background: theme.colors.background, borderRadius: 8, border: `1px solid ${theme.colors.border}` }}>
            <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.gray900, marginBottom: 8 }}>Payment Structure</div>
            {(() => {
              const paidAmount = currentOrder.total - currentOrder.due;
              return (
                <div style={{ display: 'grid', gap: 8 }}>
                  {currentOrderMilestones.map((m: any, idx: number) => {
                    const isFullyPaid = paidAmount >= m.threshold;
                    const partialPaid = paidAmount > (idx > 0 ? currentOrderMilestones[idx - 1].threshold : 0) && !isFullyPaid;
                    const milestonePaid = partialPaid ? paidAmount - (idx > 0 ? currentOrderMilestones[idx - 1].threshold : 0) : 0;
                    const milestoneTotal = m.threshold - (idx > 0 ? currentOrderMilestones[idx - 1].threshold : 0);
                    const isNextDue = !isFullyPaid && !partialPaid && paidAmount >= (idx > 0 ? currentOrderMilestones[idx - 1].threshold : 0);
                    
                    return (
                      <div 
                        key={idx} 
                        style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'flex-start', 
                          padding: '8px 10px', 
                          background: isNextDue ? theme.colors.warningLight : theme.colors.surface,
                          borderRadius: 6,
                          border: isNextDue ? `2px solid ${theme.colors.warning}` : `1px solid ${theme.colors.border}`
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ color: theme.colors.gray700, fontSize: '13px', fontWeight: 500, display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                            <span>{m.label}</span>
                            {m.invoice && (
                              <span style={{ fontSize: '10px', color: theme.colors.info, background: theme.colors.infoLight, borderRadius: 9999, padding: '2px 8px', fontWeight: 600 }}>
                                {m.invoice}
                              </span>
                            )}
                            {isNextDue && <span style={{ fontSize: '10px', color: theme.colors.warning, fontWeight: 600 }}>← NEXT DUE</span>}
                          </div>
                          <div style={{ fontSize: '11px', color: theme.colors.textSecondary, marginTop: '2px' }}>Due: {m.dueDateLabel}</div>
                          {partialPaid && (
                            <div style={{ fontSize: '11px', color: theme.colors.warning, marginTop: '2px', fontWeight: 500 }}>
                              {formatCurrency(milestonePaid, currentOrder.currency)} / {formatCurrency(milestoneTotal, currentOrder.currency)} paid
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: '12px', color: isFullyPaid ? theme.colors.success : partialPaid ? theme.colors.warning : theme.colors.textMuted, fontWeight: 500 }}>
                          {isFullyPaid ? 'Paid' : partialPaid ? 'Partially paid' : 'Pending'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>

          {/* Progress bar */}
          <div>
            <ProgressBar>
              <ProgressFill
                $percentage={
                  currentOrder.total > 0
                    ? Math.round(((currentOrder.total - currentOrder.due) / currentOrder.total) * 100)
                    : 0
                }
              />
            </ProgressBar>
            <ProgressLabel>
              {formatCurrency(currentOrder.total - currentOrder.due, currentOrder.currency)} of{' '}
              {formatCurrency(currentOrder.total, currentOrder.currency)} paid
            </ProgressLabel>
            {filteredPayments.filter(p => p.paymentAmount > 0).length > 0 && (() => {
              const last = [...filteredPayments]
                .filter(p => p.paymentAmount > 0)
                .sort((a, b) => new Date(b.processedAt).getTime() - new Date(a.processedAt).getTime())[0];
              return (
                <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                  Last payment on {formatDateOnly(last.processedAt)} · {formatCurrency(last.paymentAmount, last.currency)} · {last.paymentMethod}
                </div>
              );
            })()}
          </div>

          {/* Order-level Pay Now CTA - conditional based on structure */}
          {currentOrder.due > 0 && (() => {
            const paidAmount = currentOrder.total - currentOrder.due;
            const nextMilestone = currentOrderMilestones.find((m) => paidAmount < m.threshold);
            const hasStructure = Boolean(nextMilestone);
            
            return (
              <OrderActionBar style={{ flexDirection: 'column', alignItems: 'stretch' }}>
                {hasStructure && nextMilestone ? (
                  <>
                    <Button
                      onClick={() => {
                        const nextDueAmount = nextMilestone.threshold - paidAmount;
                        setPayNowPaymentId('ORDER_LEVEL');
                        setPayNowForm({
                          amount: nextDueAmount.toString(),
                          method: 'Bank Transfer',
                          paidBy: 'Customer',
                          paymentDate: new Date().toISOString().split('T')[0],
                          notes: `${nextMilestone.label} (${nextMilestone.percentage}%)`,
                        });
                      }}
                      style={{ width: '100%' }}
                      disabled={!canPayInvoices || currentOrder.blocked}
                      title={!canPayInvoices
                        ? 'Only permitted roles can process payments'
                        : currentOrder.blocked
                          ? currentOrder.blockedReason || 'Payment locked until shipment/customs unblock'
                          : ''}
                    >
                      {currentOrder.status === 'overdue' ? 'Pay overdue' : 'Pay next due'}: {formatCurrency(nextMilestone.threshold - paidAmount, currentOrder.currency)} – {nextMilestone.label} {nextMilestone.percentage}%
                    </Button>
                    {/* Milestone linkage confirmation */}
                    <div style={{
                      fontSize: '11px',
                      color: theme.colors.gray700,
                      padding: '8px 10px',
                      background: theme.colors.successLight,
                      border: `1px solid ${theme.colors.success}`,
                      borderRadius: 6,
                      marginTop: 6,
                      width: '100%'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 2 }}>This payment will settle:</div>
                      <div>{nextMilestone.label} ({nextMilestone.percentage}%) — {formatCurrency(nextMilestone.threshold - paidAmount, currentOrder.currency)}</div>
                      <div style={{ marginTop: 4, color: theme.colors.success, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="CheckCircle" size={11} />
                        Unlocks: {nextMilestone.label === 'Advance' ? 'Shipment release' : nextMilestone.label === 'Shipment release' ? 'Delivery confirmation' : 'Order completion'}
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setNewPaymentOrderId(currentOrder.orderId);
                        setNewPaymentForm({
                          amount: '',
                          method: 'Bank Transfer',
                          paidBy: 'Customer',
                          paymentDate: new Date().toISOString().split('T')[0],
                          notes: '',
                        });
                        setShowNewPayment(true);
                      }}
                      disabled={!canPayInvoices}
                      title={!canPayInvoices ? 'Only permitted roles can process payments' : ''}
                      style={{ width: '100%' }}
                    >
                      Record payment
                    </Button>
                    {/* Custom payment guardrails */}
                    <div style={{
                      fontSize: '10px',
                      color: theme.colors.textSecondary,
                      padding: '6px 8px',
                      background: theme.colors.warningLight,
                      border: `1px solid ${theme.colors.warning}`,
                      borderRadius: 4,
                      marginTop: 4,
                      width: '100%'
                    }}>
                      <div style={{ fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <Icon name="AlertTriangle" size={11} />
                        Custom payments may partially settle milestones
                      </div>
                      <div>Milestone impact will be calculated based on amount entered</div>
                    </div>
                  </>
                ) : (
                  <>
                    <Button
                      onClick={() => {
                        setPayNowPaymentId('ORDER_LEVEL');
                        setPayNowForm({
                          amount: currentOrder.due.toString(),
                          method: 'Bank Transfer',
                          paidBy: 'Customer',
                          paymentDate: new Date().toISOString().split('T')[0],
                          notes: '',
                        });
                      }}
                      style={{ width: '100%' }}
                      disabled={!canPayInvoices || currentOrder.blocked}
                      title={!canPayInvoices
                        ? 'Only permitted roles can process payments'
                        : currentOrder.blocked
                          ? currentOrder.blockedReason || 'Payment locked until shipment/customs unblock'
                          : ''}
                    >
                      Pay full outstanding: {formatCurrency(currentOrder.due, currentOrder.currency)}
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setNewPaymentOrderId(currentOrder.orderId);
                        setNewPaymentForm({
                          amount: '',
                          method: 'Bank Transfer',
                          paidBy: 'Customer',
                          paymentDate: new Date().toISOString().split('T')[0],
                          notes: '',
                        });
                        setShowNewPayment(true);
                      }}
                      disabled={!canPayInvoices}
                      title={!canPayInvoices ? 'Only permitted roles can process payments' : ''}
                      style={{ width: '100%' }}
                    >
                      Record payment
                    </Button>
                  </>
                )}
              </OrderActionBar>
            );
          })()}
        </OrderSummaryBox>
      )}
      {selectedOrderId !== 'ALL' && currentOrder ? (
        // Order-level payment timeline view
        <ListContainer key={`timeline-${selectedOrderId}`}>
          <div style={{
            padding: '20px 24px 0 24px',
            marginBottom: '16px',
            fontSize: '14px',
            color: theme.colors.gray800,
            fontWeight: 600,
            letterSpacing: '-0.01em'
          }}>
            Payment History
          </div>
          {filteredPayments.length === 0 ? (
            <EmptyState>No payments recorded for this order.</EmptyState>
          ) : (
            <>
            <PaymentTimeline>
              {(() => {
                const sortedPayments = [...filteredPayments]
                  .filter(p => p.paymentAmount > 0)
                  .sort((a, b) => new Date(a.initiatedAt).getTime() - new Date(b.initiatedAt).getTime());
                let remainingBalance = currentOrder.total;

                return sortedPayments.map((payment) => {
                  const balanceAfter = Math.max(remainingBalance - payment.paymentAmount, 0);
                  remainingBalance = balanceAfter;

                  return (
                    <PaymentTimelineItem key={payment.id}>
                      <TimelineDate>{formatDateOnly(payment.initiatedAt)}</TimelineDate>
                      <TimelineId>
                        {payment.id}
                        {payment.paymentLifecycle && (
                          <div style={{ fontSize: '10px', color: theme.colors.textSecondary, marginTop: '2px', display: 'flex', alignItems: 'center', gap: 4 }}>
                            {payment.paymentLifecycle.state === 'Verified' && <Icon name="CheckCircle" size={11} />}
                            {payment.paymentLifecycle.state === 'Pending verification' && <Icon name="Clock" size={11} />}
                            {payment.paymentLifecycle.state === 'Rejected' && <Icon name="XCircle" size={11} />}
                            {payment.paymentLifecycle.state === 'Initiated' && <Icon name="Pencil" size={11} />}
                            {payment.paymentLifecycle.state}
                          </div>
                        )}
                      </TimelineId>
                      <TimelineMethod>{payment.paymentMethod}</TimelineMethod>
                      <TimelineAmount>
                        {formatCurrency(payment.paymentAmount, payment.currency)}
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginTop: '4px' }}>
                          Balance after payment: {formatCurrency(balanceAfter, payment.currency)} outstanding
                          {payment.daysOverdue !== undefined && balanceAfter > 0 && payment.paymentAmount > 0 && (
                            <div style={{ fontSize: '11px', marginTop: '2px', display: 'flex', alignItems: 'center', gap: 4 }}>
                              {payment.daysOverdue === 0 ? (
                                <><Icon name="CheckCircle" size={11} /> On time</>
                              ) : (
                                <><Icon name="AlertTriangle" size={11} /> {payment.daysOverdue} days overdue</>
                              )}
                            </div>
                          )}
                        </div>
                      </TimelineAmount>
                      <TimelineStatus>
                        <StatusBadge variant={payment.paymentStatus as 'paid' | 'partial' | 'pending' | 'overdue'} label={statusLabel(payment.paymentStatus)} />
                      </TimelineStatus>
                      <TimelineAction>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setActivePaymentId(payment.id)}
                          title="View full payment details"
                        >
                          <Icon name="ChevronRight" size={14} />
                        </Button>
                      </TimelineAction>
                    </PaymentTimelineItem>
                  );
                });
              })()}
            </PaymentTimeline>
            {filteredPayments.some(p => p.paymentAmount === 0) && (
              <div style={{ padding: '16px 24px 0 24px' }}>
                <div style={{ fontSize: '14px', fontWeight: 600, color: theme.colors.gray700, marginBottom: 8 }}>Activity Timeline</div>
                <div style={{ display: 'grid', gap: 8 }}>
                  {[
                    currentOrder.due > 0
                      ? {
                          id: `due-${currentOrder.orderId}`,
                          date: currentOrder.dueDate ? formatDateOnly(currentOrder.dueDate) : 'Pending',
                          type: 'Operational',
                          event: 'Outstanding balance tracked',
                          detail: `${formatCurrency(currentOrder.due, currentOrder.currency)} remains unpaid for ${currentOrder.orderId}`,
                          icon: 'TrendingUp' as const,
                          color: theme.colors.warning,
                        }
                      : {
                          id: `settled-${currentOrder.orderId}`,
                          date: filteredPayments.length > 0 ? formatDateOnly(filteredPayments[filteredPayments.length - 1].processedAt) : 'Settled',
                          type: 'Financial',
                          event: 'Order fully settled',
                          detail: `${currentOrder.orderId} has been paid in full.`,
                          icon: 'CheckCircle' as const,
                          color: theme.colors.success,
                        },
                    currentOrder.status === 'overdue'
                      ? {
                          id: `overdue-${currentOrder.orderId}`,
                          date: currentOrder.dueDate ? formatDateOnly(currentOrder.dueDate) : 'Overdue',
                          type: 'Communication',
                          event: 'Follow-up required',
                          detail: `Payment follow-up is needed for ${currentOrder.orderId}.`,
                          icon: 'Mail' as const,
                          color: theme.colors.error,
                        }
                      : {
                          id: `status-${currentOrder.orderId}`,
                          date: filteredPayments.length > 0 ? formatDateOnly(filteredPayments[0].processedAt) : 'Open',
                          type: 'Financial',
                          event: 'Payment status updated',
                          detail: `Current status: ${currentOrder.status}.`,
                          icon: 'DollarSign' as const,
                          color: theme.colors.textSecondary,
                        },
                  ].map((activity) => (
                    <div key={activity.id} style={{ padding: '10px 12px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', color: theme.colors.gray900, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Icon name={activity.icon} size={12} /> {activity.date} • {activity.event}
                          </div>
                          <div style={{ fontSize: '11px', color: theme.colors.textSecondary }}>{activity.detail}</div>
                        </div>
                        <div style={{ fontSize: '10px', color: activity.color, fontWeight: 600, padding: '2px 6px', background: `${activity.color}15`, borderRadius: 4 }}>
                          {activity.type}
                        </div>
                      </div>
                    </div>
                  ))}
                  {filteredPayments.filter(p => p.paymentAmount === 0).map((p) => (
                    <div key={p.id} style={{ padding: '10px 12px', background: theme.colors.background, border: `1px solid ${theme.colors.border}`, borderRadius: 8 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: '12px', color: theme.colors.gray900, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
                            <Icon name="DollarSign" size={12} /> {formatDateOnly(p.processedAt)} • Payment attempt recorded
                          </div>
                          <div style={{ fontSize: '11px', color: theme.colors.textSecondary }}>No amount received</div>
                        </div>
                        <div style={{ fontSize: '10px', color: theme.colors.textSecondary, fontWeight: 600, padding: '2px 6px', background: theme.colors.gray200, borderRadius: 4 }}>
                          Financial
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            </>
          )}
        </ListContainer>
      ) : (
        // All orders view - list view only
        <ListContainer>
          <Table>
            <TableHead>
              <tr>
                <th>Order</th>
                <th>Customer</th>
                <th>Order Value</th>
                <th>Paid</th>
                <th>Outstanding</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </TableHead>
            <tbody>
              {orders.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState>No orders found.</EmptyState>
                  </TableCell>
                </TableRow>
              )}
              {[...orders]
                .sort((a, b) => statusPriority[b.status] - statusPriority[a.status])
                .map((order, index) => {
                const paidAmount = order.total - order.due;
                return (
                  <TableRow 
                    key={order.orderId} 
                    $zebra={index % 2 === 1}
                  >
                    <TableCell>{order.orderId}</TableCell>
                    <TableCell>{order.customerName}</TableCell>
                    <TableCell>{formatCurrency(order.total, order.currency)}</TableCell>
                    <TableCell>{formatCurrency(paidAmount, order.currency)}</TableCell>
                    <AmountCell style={{ color: getOutstandingColor(order.status, order.due, theme.colors) }}>
                      {formatCurrency(order.due, order.currency)}
                    </AmountCell>
                    <TableCell>
                      <StatusBadge variant={order.status as 'paid' | 'partial' | 'pending' | 'overdue'} label={statusLabel(order.status)} />
                    </TableCell>
                    <ActionCell>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {order.due > 0 && (
                          <Button
                            size="sm"
                            onClick={() => {
                              setSelectedOrderId(order.orderId);
                              setPayNowPaymentId('ORDER_LEVEL');
                              setPayNowForm({
                                amount: order.due.toString(),
                                method: 'Bank Transfer',
                                paidBy: 'Customer',
                                paymentDate: new Date().toISOString().split('T')[0],
                                notes: '',
                              });
                            }}
                          >
                            Pay
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedOrderId(order.orderId)}
                        >
                          View
                          <Icon name="ChevronRight" size={14} />
                        </Button>
                      </div>
                    </ActionCell>
                  </TableRow>
                );
              })}
            </tbody>
          </Table>
        </ListContainer>
      )}
        </LayoutMain>

        {/* Right Context Rail */}
        <LayoutSide $top={railTop} ref={railRef}>
          <CardContainer variant="elevated" padding="md">
            <SectionHeading icon="Users" size="sm" style={{ marginBottom: 16 }}>People Involved</SectionHeading>
            {selectedOrderId !== 'ALL' && currentOrder && (
              <div style={{
                fontSize: 12,
                color: theme.colors.textSecondary,
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: `1px solid ${theme.colors.border}`
              }}>
                Related to <strong>{currentOrder.orderId}</strong>
              </div>
            )}
            <div style={{ 
              display: 'flex',
              flexDirection: 'column',
              gap: 16
            }}>
              {paymentContacts.financeOwner && (
                <div>
                  <SectionHeading
                    icon="DollarSign"
                    size="sm"
                    style={{ marginBottom: 12 }}
                    action={<span style={{ fontSize: 10, color: theme.colors.textMuted, textTransform: 'none' }}>Internal</span>}
                  >
                    Payment Owner
                  </SectionHeading>
                  {hasOverduePayments && (
                    <div style={{
                      fontSize: 11,
                      color: theme.colors.warningDark,
                      marginBottom: 12,
                      padding: '6px 10px',
                      background: theme.colors.warningLight,
                      borderRadius: 6,
                      fontWeight: 500,
                      border: `1px solid ${theme.colors.warning}`,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6
                    }}>
                      <Icon name="AlertTriangle" size={12} />
                      Overdue payment linked
                    </div>
                  )}
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                    <div style={{
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      background: theme.colors.primary,
                      color: theme.colors.surface,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 600,
                      fontSize: 14,
                      flexShrink: 0
                    }}>
                      {paymentContacts.financeOwner.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                    </div>
                    <div style={{ flex: 1, overflow: 'hidden' }}>
                      <div style={{
                        fontWeight: 600,
                        fontSize: 14,
                        color: theme.colors.gray800,
                        marginBottom: 3,
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                        overflowWrap: 'break-word'
                      }}>
                        {paymentContacts.financeOwner.organization}
                      </div>
                      <div style={{
                        fontSize: 13,
                        color: theme.colors.textSecondary,
                        marginBottom: 8,
                        whiteSpace: 'normal',
                        wordWrap: 'break-word'
                      }}>
                        {paymentContacts.financeOwner.name}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: theme.colors.textSecondary,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6
                      }}>
                        <a href={`mailto:${paymentContacts.financeOwner.email}`} style={{
                          color: theme.colors.primary,
                          textDecoration: 'none',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word'
                        }}>
                          {paymentContacts.financeOwner.email}
                        </a>
                        {paymentContacts.financeOwner.phone && (
                          <div style={{ whiteSpace: 'nowrap' }}>{paymentContacts.financeOwner.phone}</div>
                        )}
                      </div>
                    </div>
                  </div>
                  {hasOverduePayments && (
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={() => console.log('Reminder sent')}
                      style={{ width: '100%', marginTop: 12, fontSize: 12 }}
                    >
                      Send Reminder
                    </Button>
                  )}
                </div>
              )}
              {selectedOrderId !== 'ALL' && currentOrder && (paymentContacts.financeOwner || paymentContacts.billingContact) && (
                <div style={{ display: 'grid', gap: 16 }}>
                  <SectionHeading icon="Users" size="sm">Payment Contacts</SectionHeading>

                  {paymentContacts.financeOwner && (
                    <div>
                      <SectionHeading icon="DollarSign" size="sm" style={{ marginBottom: 8 }}>Finance Owner</SectionHeading>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: theme.colors.success,
                          color: theme.colors.surface,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: 13,
                          flexShrink: 0
                        }}>
                          {paymentContacts.financeOwner.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{
                            fontSize: 12,
                            color: theme.colors.gray800,
                            fontWeight: 600,
                            marginBottom: 4,
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}>
                            {paymentContacts.financeOwner.name}
                          </div>
                          <a href={`mailto:${paymentContacts.financeOwner.email}`} style={{
                            fontSize: 11,
                            color: theme.colors.primary,
                            textDecoration: 'none',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            display: 'block',
                            marginBottom: 4
                          }}>
                            {paymentContacts.financeOwner.email}
                          </a>
                          {paymentContacts.financeOwner.phone && (
                            <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>
                              {paymentContacts.financeOwner.phone}
                            </div>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            style={{ marginTop: 8, paddingLeft: 0 }}
                            onClick={() => onOpenMessaging?.({ orderId: selectedOrderId, contactName: paymentContacts.financeOwner?.name, contactEmail: paymentContacts.financeOwner?.email })}
                          >
                            <Icon name="Mail" size={14} />
                            Message
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  {paymentContacts.billingContact && (
                    <div>
                      <SectionHeading icon="Clipboard" size="sm" style={{ marginBottom: 8 }}>Billing Contact (External)</SectionHeading>
                      <div style={{ fontSize: 11, color: theme.colors.textSecondary, marginBottom: 8 }}>
                        {currentOrder.customerName}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                        <div style={{
                          width: 36,
                          height: 36,
                          borderRadius: '50%',
                          background: theme.colors.info,
                          color: theme.colors.surface,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontWeight: 600,
                          fontSize: 13,
                          flexShrink: 0
                        }}>
                          {paymentContacts.billingContact.name.split(' ').map(n => n[0]).join('').toUpperCase()}
                        </div>
                        <div style={{ flex: 1, overflow: 'hidden' }}>
                          <div style={{
                            fontSize: 12,
                            color: theme.colors.gray800,
                            fontWeight: 600,
                            marginBottom: 4,
                            whiteSpace: 'normal',
                            wordWrap: 'break-word'
                          }}>
                            {paymentContacts.billingContact.name}
                          </div>
                          <a href={`mailto:${paymentContacts.billingContact.email}`} style={{
                            fontSize: 11,
                            color: theme.colors.primary,
                            textDecoration: 'none',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            display: 'block',
                            marginBottom: 4
                          }}>
                            {paymentContacts.billingContact.email}
                          </a>
                          {paymentContacts.billingContact.phone && (
                            <div style={{ fontSize: 11, color: theme.colors.textSecondary }}>
                              {paymentContacts.billingContact.phone}
                            </div>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            style={{ marginTop: 8, paddingLeft: 0 }}
                            onClick={() => onOpenMessaging?.({ orderId: selectedOrderId, contactName: paymentContacts.billingContact?.name, contactEmail: paymentContacts.billingContact?.email })}
                          >
                            <Icon name="Mail" size={14} />
                            Message
                          </Button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
              {selectedOrderId === 'ALL' && (
                <ContactPanel title="External (Customer)" icon="User" contacts={visibleContacts.external} />
              )}
            </div>
          </CardContainer>
        </LayoutSide>
      </LayoutGridComponent>

      {activePayment && (
        <DrawerOverlay onClick={() => setActivePaymentId(null)}>
          <DrawerPanel onClick={(e) => e.stopPropagation()}>
            {/* View Details Header */}
            <DrawerHeader>
              <div>
                <DrawerTitle>Payment: {activePayment.id}</DrawerTitle>
                <div style={{ marginTop: '8px', display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <StatusBadge variant={activePayment.paymentStatus as 'paid' | 'partial' | 'pending' | 'overdue'} label={statusLabel(activePayment.paymentStatus)} />
                  <DrawerLabel>Linked Order {activePayment.orderId}</DrawerLabel>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const receipt = `Payment Receipt\n\nPayment ID: ${activePayment.id}\nOrder: ${activePayment.orderId}\nCustomer: ${activePayment.customerName}\nAmount: ${formatCurrency(activePayment.paymentAmount, activePayment.currency)}\nDate: ${formatDateTime(activePayment.processedAt)}\n\nThank you for your business.`;
                    const blob = new Blob([receipt], { type: 'text/plain' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = `${activePayment.id}-receipt.txt`;
                    a.click();
                  }}
                >
                  <Icon name="Download" size={14} />
                  Receipt
                </Button>
                <DrawerClose onClick={() => setActivePaymentId(null)} aria-label="Close details">
                  <Icon name="X" size={16} />
                </DrawerClose>
              </div>
            </DrawerHeader>

            <DrawerContent>
              {/* A. Parties & Method */}
              <DrawerSection>
                <DrawerLabel>Parties & Method</DrawerLabel>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Customer</DrawerLabel>
                    <DrawerValue>{activePayment.customerName}</DrawerValue>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Paid By</DrawerLabel>
                    <DrawerValue>{activePayment.paidBy}</DrawerValue>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Payment Method</DrawerLabel>
                    <DrawerValue>{activePayment.paymentMethod}</DrawerValue>
                  </div>
                </div>
              </DrawerSection>

              {/* B. Timeline */}
              <DrawerSection>
                <DrawerLabel>Timeline</DrawerLabel>
                <div style={{ display: 'grid', gap: '14px' }}>
                  <div>
                    <DrawerValue style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 4 }}>Initiated</DrawerValue>
                    <DrawerValue style={{ display: 'block', marginBottom: 4 }}>{formatDateTime(activePayment.initiatedAt)}</DrawerValue>
                    <DrawerValue style={{ display: 'block', fontSize: '12px', color: theme.colors.textSecondary }}>By {activePayment.initiatedBy}</DrawerValue>
                  </div>
                  <div>
                    <DrawerValue style={{ display: 'block', fontSize: '11px', textTransform: 'uppercase', fontWeight: 600, color: theme.colors.textSecondary, marginBottom: 4 }}>Processed</DrawerValue>
                    <DrawerValue style={{ display: 'block', marginBottom: 4 }}>{formatDateTime(activePayment.processedAt)}</DrawerValue>
                    <DrawerValue style={{ display: 'block', fontSize: '12px', color: theme.colors.textSecondary }}>By {activePayment.processedBy}</DrawerValue>
                  </div>
                </div>
              </DrawerSection>

              {/* C. Amounts */}
              <DrawerSection>
                <DrawerLabel>Amounts</DrawerLabel>
                <div style={{ display: 'grid', gap: '8px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Total Amount</DrawerLabel>
                    <DrawerValue>{formatCurrency(activePayment.totalOrderAmount, activePayment.currency)}</DrawerValue>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Paid Amount</DrawerLabel>
                    <DrawerValue>{formatCurrency(activePayment.paymentAmount, activePayment.currency)}</DrawerValue>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Due Amount</DrawerLabel>
                    <DrawerValue>{formatCurrency(activePayment.dueAmount, activePayment.currency)}</DrawerValue>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Currency</DrawerLabel>
                    <DrawerValue>{activePayment.currency}</DrawerValue>
                  </div>
                </div>
              </DrawerSection>

              {/* STEP 2: Payment Lifecycle */}
              {activePayment.paymentLifecycle && (
                <PaymentLifecycleCard>
                  <LifecycleState>
                    {activePayment.paymentLifecycle.state === 'Verified' && <Icon name="CheckCircle" size={14} />}
                    {activePayment.paymentLifecycle.state === 'Pending verification' && <Icon name="Clock" size={14} />}
                    {activePayment.paymentLifecycle.state === 'Rejected' && <Icon name="XCircle" size={14} />}
                    {activePayment.paymentLifecycle.state === 'Initiated' && <Icon name="Pencil" size={14} />}
                    {activePayment.paymentLifecycle.state}
                  </LifecycleState>
                  <LifecycleTimeline>
                    <LifecycleStep $completed={true}>
                      <div>
                        <LifecycleStepTitle>Payment Initiated</LifecycleStepTitle>
                        <LifecycleStepDetail>{formatDateTime(activePayment.paymentLifecycle.initiatedAt)}</LifecycleStepDetail>
                      </div>
                    </LifecycleStep>
                    {activePayment.paymentLifecycle.verificationSubmittedAt && (
                      <LifecycleStep $completed={true}>
                        <div>
                          <LifecycleStepTitle>Submitted for Verification</LifecycleStepTitle>
                          <LifecycleStepDetail>{formatDateTime(activePayment.paymentLifecycle.verificationSubmittedAt)}</LifecycleStepDetail>
                        </div>
                      </LifecycleStep>
                    )}
                    {activePayment.paymentLifecycle.verifiedAt && (
                      <LifecycleStep $completed={true}>
                        <div>
                          <LifecycleStepTitle>Verified by Finance</LifecycleStepTitle>
                          <LifecycleStepDetail>{formatDateTime(activePayment.paymentLifecycle.verifiedAt)} • {activePayment.paymentLifecycle.verifiedBy}</LifecycleStepDetail>
                        </div>
                      </LifecycleStep>
                    )}
                    {activePayment.paymentLifecycle.rejectedAt && (
                      <LifecycleStep $completed={false}>
                        <div>
                          <LifecycleStepTitle>Rejected</LifecycleStepTitle>
                          <LifecycleStepDetail>{formatDateTime(activePayment.paymentLifecycle.rejectedAt)} • {activePayment.paymentLifecycle.rejectionReason}</LifecycleStepDetail>
                        </div>
                      </LifecycleStep>
                    )}
                  </LifecycleTimeline>
                </PaymentLifecycleCard>
              )}

              {/* STEP 4: Due Date & Aging */}
              {(activePayment.invoiceDate || activePayment.dueDate || activePayment.paymentTerms) && (
                <DueDateCard>
                  {activePayment.invoiceDate && (
                    <DueDateItem>
                      <DueDateLabel>Invoice Date</DueDateLabel>
                      <DueDateValue>{new Date(activePayment.invoiceDate).toLocaleDateString()}</DueDateValue>
                    </DueDateItem>
                  )}
                  {activePayment.paymentTerms && (
                    <DueDateItem>
                      <DueDateLabel>Payment Terms</DueDateLabel>
                      <DueDateValue>{activePayment.paymentTerms}</DueDateValue>
                    </DueDateItem>
                  )}
                  {activePayment.dueDate && (
                    <DueDateItem>
                      <DueDateLabel>Due Date</DueDateLabel>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <DueDateValue>{new Date(activePayment.dueDate).toLocaleDateString()}</DueDateValue>
                        {activePayment.daysOverdue !== undefined && activePayment.paymentStatus !== 'paid' && activePayment.dueAmount > 0 && (
                          <AgingBadge $daysOverdue={activePayment.daysOverdue}>
                            {activePayment.daysOverdue === 0 ? (
                              <><Icon name="CheckCircle" size={11} /> On time</>
                            ) : (
                              <>{activePayment.daysOverdue} days overdue</>
                            )}
                          </AgingBadge>
                        )}
                      </div>
                    </DueDateItem>
                  )}
                </DueDateCard>
              )}

              {/* STEP 5: Payment Methods */}
              {activePayment.availablePaymentMethods && (
                <PaymentMethodsPanel>
                  <DrawerLabel>Payment Methods</DrawerLabel>
                  {activePayment.availablePaymentMethods.map((method, idx) => (
                    <PaymentMethodButton key={idx} $active={method.active} disabled={!method.active}>
                      <span>{method.method}</span>
                      <MethodBadge $active={method.active}>
                        {method.active ? 'Available' : method.reason || 'Unavailable'}
                      </MethodBadge>
                    </PaymentMethodButton>
                  ))}
                </PaymentMethodsPanel>
              )}

              {/* STEP 6: Downstream Blocks */}
              {activePayment.downstreamBlocks && activePayment.downstreamBlocks.length > 0 && (
                <DownstreamBlocksCard>
                  <SectionHeading icon="AlertTriangle" size="sm">Downstream Impact</SectionHeading>
                  <BlocksList>
                    {activePayment.downstreamBlocks.map((block, idx) => (
                      <li key={idx}>
                        <strong>{block.blockedOperation}</strong>
                        {block.blockedBy === 'payment' && ' (blocked by outstanding payment)'}
                        {block.blockedBy === 'document' && ' (blocked by pending documents)'}
                        {block.blockedBy === 'customs' && ' (blocked by customs)'}
                        {block.expectedUnblockAt && (
                          <div style={{ fontSize: '11px', marginTop: '4px' }}>
                            Expected to unblock: {formatDateTime(block.expectedUnblockAt)}
                          </div>
                        )}
                      </li>
                    ))}
                  </BlocksList>
                </DownstreamBlocksCard>
              )}

              {/* D. Notes */}
              <DrawerSection>
                <DrawerLabel>Notes</DrawerLabel>
                <DrawerValue style={{ fontSize: '13px', fontWeight: 400, color: theme.colors.gray700 }}>
                  {activePayment.notes || 'No notes added.'}
                </DrawerValue>
              </DrawerSection>

              {/* STEP 2: Payment Type (old section - keeping for backwards compat) */}
              {activePayment.paymentType && (
                <DrawerSection>
                  <DrawerLabel>Payment Type</DrawerLabel>
                  <div style={{ display: 'grid', gap: '8px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Type</DrawerLabel>
                      <DrawerValue>{activePayment.paymentType}</DrawerValue>
                    </div>
                    {activePayment.creditTerm && (
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <DrawerLabel style={{ textTransform: 'none', fontSize: '12px', fontWeight: 400 }}>Credit Term</DrawerLabel>
                        <DrawerValue>{activePayment.creditTerm}</DrawerValue>
                      </div>
                    )}
                  </div>
                </DrawerSection>
              )}

              {/* STEP 1: Payment Impact Banner */}
              {activePayment.blockingImpact && (
                <ImpactBanner $isBlocked={activePayment.blockingImpact.isBlocked}>
                  <div style={{ fontSize: '14px', fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '8px', color: activePayment.blockingImpact.isBlocked ? theme.colors.errorDark : theme.colors.successDark }}>
                    <Icon name={activePayment.blockingImpact.isBlocked ? 'AlertTriangle' : 'CheckCircle'} size={16} />
                    {activePayment.blockingImpact.isBlocked ? 'PAYMENT IMPACT' : 'No operational blocks'}
                  </div>
                  {activePayment.blockingImpact.isBlocked && (
                    <div style={{ fontSize: '13px', color: theme.colors.errorDark }}>
                      This payment currently blocks:
                      <ul style={{ margin: '8px 0 0 16px', padding: 0, listStyle: 'none' }}>
                        {activePayment.blockingImpact.blockedOperations.map((op, idx) => (
                          <li key={idx} style={{ padding: '4px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <span>•</span> {op}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </ImpactBanner>
              )}

              {/* STEP 3: Why Payment is Required */}
              {activePayment.paymentReason && (
                <WhyPaymentSection>
                  <SectionHeading icon="Info" size="sm">Why This Payment is Required</SectionHeading>
                  <WhyPaymentList>
                    {activePayment.paymentReason.milestone && (
                      <li>Shipment reached milestone: {activePayment.paymentReason.milestone}</li>
                    )}
                    {activePayment.paymentReason.terms && (
                      <li>As per terms: {activePayment.paymentReason.terms}</li>
                    )}
                    {activePayment.paymentReason.invoiceNumber && (
                      <li>Invoice {activePayment.paymentReason.invoiceNumber} issued on {activePayment.paymentReason.invoiceDate ? new Date(activePayment.paymentReason.invoiceDate).toLocaleDateString() : 'N/A'}</li>
                    )}
                    {activePayment.paymentReason.creditExpiredDate && (
                      <li>Credit period expired on {new Date(activePayment.paymentReason.creditExpiredDate).toLocaleDateString()}</li>
                    )}
                  </WhyPaymentList>
                </WhyPaymentSection>
              )}

              {/* STEP 4: Partial Payment Logic */}
              {activePayment.partialPaymentRules && (
                <PaymentRulePanel>
                  <SectionHeading icon="Percent" size="sm">Payment Rule</SectionHeading>
                  <RuleAmounts>
                    <RuleAmountItem>
                      <RuleAmountLabel>Total Required to Unblock</RuleAmountLabel>
                      <RuleAmountValue>{formatCurrency(activePayment.partialPaymentRules.totalRequiredToUnblock, activePayment.currency)}</RuleAmountValue>
                    </RuleAmountItem>
                    <RuleAmountItem>
                      <RuleAmountLabel>Paid So Far</RuleAmountLabel>
                      <RuleAmountValue>{formatCurrency(activePayment.partialPaymentRules.paidSoFar, activePayment.currency)}</RuleAmountValue>
                    </RuleAmountItem>
                    <RuleAmountItem>
                      <RuleAmountLabel>Remaining to Unblock</RuleAmountLabel>
                      <RuleAmountValue style={{ color: theme.colors.error }}>{formatCurrency(activePayment.partialPaymentRules.remainingToUnblock, activePayment.currency)}</RuleAmountValue>
                    </RuleAmountItem>
                  </RuleAmounts>
                  <RuleStatus>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.success, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="CheckCircle" size={13} /> What is unblocked already
                      </div>
                      <RuleStatusList>
                        {activePayment.partialPaymentRules.unblocked.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </RuleStatusList>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: 600, color: theme.colors.error, marginBottom: '8px', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <Icon name="XCircle" size={13} /> What is still blocked
                      </div>
                      <RuleStatusList>
                        {activePayment.partialPaymentRules.stillBlocked.map((item, idx) => (
                          <li key={idx}>{item}</li>
                        ))}
                      </RuleStatusList>
                    </div>
                  </RuleStatus>
                </PaymentRulePanel>
              )}

              {/* STEP 5: Payment Verification */}
              {activePayment.verification && (
                <VerificationSection>
                  <SectionHeading icon="CreditCard" size="sm">Payment Verification</SectionHeading>
                  <VerificationGrid>
                    <VerificationItem>
                      <VerificationLabel>Method</VerificationLabel>
                      <VerificationValue>{activePayment.verification.method}</VerificationValue>
                    </VerificationItem>
                    {activePayment.verification.reference && (
                      <VerificationItem>
                        <VerificationLabel>Reference / UTR</VerificationLabel>
                        <VerificationValue>{activePayment.verification.reference}</VerificationValue>
                      </VerificationItem>
                    )}
                    {activePayment.verification.paidOn && (
                      <VerificationItem>
                        <VerificationLabel>Paid On</VerificationLabel>
                        <VerificationValue>{new Date(activePayment.verification.paidOn).toLocaleDateString()}</VerificationValue>
                      </VerificationItem>
                    )}
                    {activePayment.verification.proofUrl && (
                      <VerificationItem>
                        <VerificationLabel>Uploaded Proof</VerificationLabel>
                        <VerificationValue>
                          <a href={activePayment.verification.proofUrl} style={{ color: theme.colors.primary, textDecoration: 'none' }}>
                            View →
                          </a>
                        </VerificationValue>
                      </VerificationItem>
                    )}
                    <VerificationItem>
                      <VerificationLabel>Reconciliation Status</VerificationLabel>
                      <StatusBadge
                        variant={
                          activePayment.verification.reconciliationStatus === 'Matched'
                            ? 'success'
                            : activePayment.verification.reconciliationStatus === 'Disputed'
                              ? 'error'
                              : 'pending'
                        }
                        label={activePayment.verification.reconciliationStatus}
                        dot
                      />
                    </VerificationItem>
                    {activePayment.verification.verifiedBy && (
                      <VerificationItem>
                        <VerificationLabel>Verified By</VerificationLabel>
                        <VerificationValue>{activePayment.verification.verifiedBy}</VerificationValue>
                      </VerificationItem>
                    )}
                    {activePayment.verification.verifiedAt && (
                      <VerificationItem>
                        <VerificationLabel>Verified At</VerificationLabel>
                        <VerificationValue>{formatDateTime(activePayment.verification.verifiedAt)}</VerificationValue>
                      </VerificationItem>
                    )}
                  </VerificationGrid>
                </VerificationSection>
              )}

              {/* STEP 6: People Involved */}
              {(activePayment.paymentOwner || activePayment.billingContact || activePayment.escalationContact) && (
                <div>
                  <DrawerLabel style={{ marginBottom: '12px' }}>People Involved</DrawerLabel>
                  <PeopleSection>
                    {activePayment.paymentOwner && (
                      <PersonCard>
                        <PersonRole>Payment Owner (Internal)</PersonRole>
                        <PersonName>{activePayment.paymentOwner.name}</PersonName>
                        <PersonDetails>
                          <div>{activePayment.paymentOwner.role}</div>
                          <div><a href={`mailto:${activePayment.paymentOwner.email}`}>{activePayment.paymentOwner.email}</a></div>
                          {activePayment.paymentOwner.phone && <div>{activePayment.paymentOwner.phone}</div>}
                        </PersonDetails>
                      </PersonCard>
                    )}
                    {activePayment.billingContact && (
                      <PersonCard>
                        <PersonRole>Billing Contact (External)</PersonRole>
                        <PersonName>{activePayment.billingContact.name}</PersonName>
                        <PersonDetails>
                          {activePayment.billingContact.organization && <div>{activePayment.billingContact.organization}</div>}
                          <div><a href={`mailto:${activePayment.billingContact.email}`}>{activePayment.billingContact.email}</a></div>
                          {activePayment.billingContact.phone && <div>{activePayment.billingContact.phone}</div>}
                        </PersonDetails>
                      </PersonCard>
                    )}
                    {activePayment.escalationContact && (
                      <PersonCard>
                        <PersonRole>Escalation Contact</PersonRole>
                        <PersonName>{activePayment.escalationContact.name}</PersonName>
                        <PersonDetails>
                          <div><a href={`mailto:${activePayment.escalationContact.email}`}>{activePayment.escalationContact.email}</a></div>
                          {activePayment.escalationContact.phone && <div>{activePayment.escalationContact.phone}</div>}
                          {activePayment.escalationContact.escalationDaysThreshold && (
                            <div style={{ marginTop: '4px', fontSize: '11px', color: theme.colors.textSecondary }}>
                              Escalates if overdue &gt; {activePayment.escalationContact.escalationDaysThreshold} days
                            </div>
                          )}
                        </PersonDetails>
                      </PersonCard>
                    )}
                  </PeopleSection>
                </div>
              )}
            </DrawerContent>

            {/* Footer Actions */}
            <DrawerActions>
              <Button
                onClick={() => {
                  const receipt = `Payment Receipt\n\nPayment ID: ${activePayment.id}\nOrder: ${activePayment.orderId}\nCustomer: ${activePayment.customerName}\nAmount: ${formatCurrency(activePayment.paymentAmount, activePayment.currency)}\nDate: ${formatDateTime(activePayment.processedAt)}\n\nThank you for your business.`;
                  const blob = new Blob([receipt], { type: 'text/plain' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `${activePayment.id}-receipt.txt`;
                  a.click();
                }}
              >
                <Icon name="Download" size={16} />
                Download Receipt
              </Button>
              <Button variant="ghost" onClick={() => setActivePaymentId(null)}>
                Close
              </Button>
            </DrawerActions>
          </DrawerPanel>
        </DrawerOverlay>
      )}

      {/* Pay Now Modal - Order Level or Individual Payment */}
      {payNowPaymentId && (payNowPaymentId === 'ORDER_LEVEL' || payNowPaymentId === 'ORDER_LEVEL_PARTIAL' || paymentRecords.find(p => p.id === payNowPaymentId)) && (
        <ModalOverlay onClick={() => setPayNowPaymentId(null)}>
          <ModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <div>
                <ModalTitle>Pay Now</ModalTitle>
                {payNowPaymentId === 'ORDER_LEVEL' || payNowPaymentId === 'ORDER_LEVEL_PARTIAL' ? (
                  <ModalSubtitle>
                    Order: {currentOrder?.orderId} · {currentOrder?.customerName}
                  </ModalSubtitle>
                ) : (
                  <ModalSubtitle>
                    Order: {paymentRecords.find(p => p.id === payNowPaymentId)?.orderId} · {paymentRecords.find(p => p.id === payNowPaymentId)?.customerName}
                  </ModalSubtitle>
                )}
              </div>
              <button onClick={() => setPayNowPaymentId(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textSecondary, display: 'inline-flex' }}><Icon name="X" size={20} /></button>
            </ModalHeader>

            <ModalContent>
              {payNowPaymentId === 'ORDER_LEVEL' || payNowPaymentId === 'ORDER_LEVEL_PARTIAL' ? (
                // Order-level payment
                currentOrder && (
                  <>
                    <SummaryBox>
                      <SummaryRow>
                        <SummaryLabel>Outstanding Amount</SummaryLabel>
                        <SummaryValue>{formatCurrency(currentOrder.due, currentOrder.currency)}</SummaryValue>
                      </SummaryRow>
                    </SummaryBox>

                    <FormGroup>
                      <FormLabel>Amount to Pay *</FormLabel>
                      <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                        <span style={{ position: 'absolute', left: '12px', color: theme.colors.textSecondary, fontWeight: 600 }}>₹</span>
                        <Input
                          type="number"
                          placeholder={currentOrder.due.toString()}
                          value={payNowForm.amount || (payNowPaymentId === 'ORDER_LEVEL' ? currentOrder.due : '')}
                          onChange={(e) => setPayNowForm({ ...payNowForm, amount: e.target.value })}
                          style={{ paddingLeft: '32px' }}
                          max={currentOrder.due}
                        />
                      </div>
                      <small style={{ color: theme.colors.textSecondary, fontSize: '12px' }}>
                        Maximum: {formatCurrency(currentOrder.due, currentOrder.currency)}
                      </small>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Payment Method *</FormLabel>
                      <Select
                        value={payNowForm.method}
                        onChange={(e) => {
                          setPayNowForm({ 
                            ...payNowForm, 
                            method: e.target.value,
                            bankName: undefined,
                            transactionReference: undefined,
                            transferDate: undefined,
                            accountLast4: undefined,
                            cardType: undefined,
                            cardLast4: undefined,
                            authorizationId: undefined,
                          });
                        }}
                      >
                        <option value="Bank Transfer">Bank Transfer</option>
                        <option value="Credit Card">Credit Card</option>
                        <option value="Debit Card">Debit Card</option>
                        <option value="Wire Transfer">Wire Transfer</option>
                        <option value="UPI">UPI</option>
                        <option value="Wallet">Wallet</option>
                        <option value="Cash">Cash</option>
                        <option value="Check">Check</option>
                      </Select>
                    </FormGroup>

                    <MethodSpecificFields
                      method={payNowForm.method}
                      formData={payNowForm as PaymentFormState}
                      onFormChange={(data) => setPayNowForm({ ...payNowForm, ...data })}
                    />

                    <FormGroup>
                      <FormLabel>Paid By *</FormLabel>
                      <Select
                        value={payNowForm.paidBy}
                        onChange={(e) => setPayNowForm({ ...payNowForm, paidBy: e.target.value as any })}
                      >
                        <option value="Customer">Customer</option>
                        <option value="Agent">Agent</option>
                        <option value="Admin">Admin</option>
                      </Select>
                    </FormGroup>

                    <FormGroup>
                      <FormLabel>Notes (Optional)</FormLabel>
                      <FormTextarea
                        placeholder="Add any notes about this payment..."
                        value={payNowForm.notes}
                        onChange={(e) => setPayNowForm({ ...payNowForm, notes: e.target.value })}
                      />
                    </FormGroup>

                    {/* STEP 4: Bank Transfer Proof Upload */}
                    {payNowForm.method === 'Bank Transfer' && (
                      <FormGroup>
                        <FormLabel>Payment Proof (Recommended)</FormLabel>
                        <div style={{ fontSize: '12px', color: theme.colors.textSecondary, marginBottom: '8px' }}>
                          Upload bank slip, payment advice, or Swift/UTR proof for faster verification
                        </div>
                        <Input
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            // File upload handling - mark as pending verification
                            console.log('Payment proof uploaded:', e.target.files?.[0]?.name);
                          }}
                        />
                        <div style={{ fontSize: '11px', color: theme.colors.textMuted, marginTop: '4px' }}>
                          Payment will be marked as "Pending verification" until finance team confirms
                        </div>
                      </FormGroup>
                    )}
                  </>
                )
              ) : (
                // Individual payment - for backward compatibility
                payNowPaymentId && paymentRecords.find(p => p.id === payNowPaymentId) && (() => {
                  const payment = paymentRecords.find(p => p.id === payNowPaymentId)!;
                  return (
                    <>
                      <SummaryBox>
                        <SummaryRow>
                          <SummaryLabel>Due Amount</SummaryLabel>
                          <SummaryValue>{formatCurrency(payment.dueAmount, payment.currency)}</SummaryValue>
                        </SummaryRow>
                      </SummaryBox>

                      <FormGroup>
                        <FormLabel>Amount to Pay *</FormLabel>
                        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                          <span style={{ position: 'absolute', left: '12px', color: theme.colors.textSecondary, fontWeight: 600 }}>₹</span>
                          <Input
                            type="number"
                            placeholder={payment.dueAmount.toString()}
                            value={payNowForm.amount || payment.dueAmount}
                            onChange={(e) => setPayNowForm({ ...payNowForm, amount: e.target.value })}
                            style={{ paddingLeft: '32px' }}
                            max={payment.dueAmount}
                          />
                        </div>
                      </FormGroup>

                      <FormGroup>
                        <FormLabel>Payment Method *</FormLabel>
                        <Select
                          value={payNowForm.method}
                          onChange={(e) => {
                            setPayNowForm({ 
                              ...payNowForm, 
                              method: e.target.value,
                              bankName: undefined,
                              transactionReference: undefined,
                              transferDate: undefined,
                              accountLast4: undefined,
                              cardType: undefined,
                              cardLast4: undefined,
                              authorizationId: undefined,
                            });
                          }}
                        >
                          <option value="Bank Transfer">Bank Transfer</option>
                          <option value="Credit Card">Credit Card</option>
                          <option value="Debit Card">Debit Card</option>
                          <option value="Wire Transfer">Wire Transfer</option>
                          <option value="UPI">UPI</option>
                          <option value="Wallet">Wallet</option>
                          <option value="Cash">Cash</option>
                          <option value="Check">Check</option>
                        </Select>
                      </FormGroup>

                      <MethodSpecificFields
                        method={payNowForm.method}
                        formData={payNowForm as PaymentFormState}
                        onFormChange={(data) => setPayNowForm({ ...payNowForm, ...data })}
                      />

                      <FormGroup>
                        <FormLabel>Paid By *</FormLabel>
                        <Select
                          value={payNowForm.paidBy}
                          onChange={(e) => setPayNowForm({ ...payNowForm, paidBy: e.target.value as any })}
                        >
                          <option value="Customer">Customer</option>
                          <option value="Agent">Agent</option>
                          <option value="Admin">Admin</option>
                        </Select>
                      </FormGroup>

                      <FormGroup>
                        <FormLabel>Notes (Optional)</FormLabel>
                        <FormTextarea
                          placeholder="Add any notes about this payment..."
                          value={payNowForm.notes}
                          onChange={(e) => setPayNowForm({ ...payNowForm, notes: e.target.value })}
                        />
                      </FormGroup>
                    </>
                  );
                })()
              )}
            </ModalContent>

            <ModalFooter>
              <Button variant="ghost" onClick={() => setPayNowPaymentId(null)}>Cancel</Button>
              <Button
                disabled={isSubmittingPayment}
                onClick={async () => {
                  if (!payNowForm.amount || isSubmittingPayment) {
                    return;
                  }

                  const orderIdForPayment = (() => {
                    if (payNowPaymentId === 'ORDER_LEVEL' || payNowPaymentId === 'ORDER_LEVEL_PARTIAL') {
                      return currentOrder?.orderId;
                    }
                    return paymentRecords.find((p) => p.id === payNowPaymentId)?.orderId;
                  })();

                  if (!orderIdForPayment) {
                    return;
                  }

                  try {
                    setIsSubmittingPayment(true);
                    const amount = await persistPayment(orderIdForPayment, payNowForm);

                    logAudit('payments.pay', role,
                      { orderId: orderIdForPayment, amount: String(amount) },
                      { status: 'paid', method: payNowForm.method }
                    );

                    const currentDue = currentOrder?.due;
                    const updatedOutstanding = typeof currentDue === 'number'
                      ? Math.max(currentDue - amount, 0)
                      : undefined;
                    const message = updatedOutstanding !== undefined
                      ? `Payment of ${formatCurrency(amount, currentOrder?.currency || 'INR')} recorded successfully. Outstanding balance: ${formatCurrency(updatedOutstanding, currentOrder?.currency || 'INR')}`
                      : `Payment recorded successfully.`;

                    setPayNowPaymentId(null);
                    setActivePaymentId(null);
                    setSelectedOrderId(orderIdForPayment);
                    if (onPaymentSuccess) {
                      onPaymentSuccess(orderIdForPayment, message);
                    }
                  } finally {
                    setIsSubmittingPayment(false);
                  }
                }}
              >
                {isSubmittingPayment ? 'Saving...' : 'Confirm Payment'}
              </Button>
            </ModalFooter>
          </ModalPanel>
        </ModalOverlay>
      )}

      {/* Record Payment Modal */}
      {showNewPayment && (
        <ModalOverlay onClick={() => setShowNewPayment(false)}>
          <ModalPanel onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>Record Payment</ModalTitle>
              <button onClick={() => setShowNewPayment(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.textSecondary, display: 'inline-flex' }}><Icon name="X" size={20} /></button>
            </ModalHeader>

            <ModalContent>
              {/* Section 1: Order Selection */}
              <FormGroup>
                <FormLabel>Select Order *</FormLabel>
                <Select
                  value={newPaymentOrderId}
                  onChange={(e) => {
                    setNewPaymentOrderId(e.target.value);
                    const selected = orders.find(o => o.orderId === e.target.value);
                    if (selected) {
                      setNewPaymentForm({
                        ...newPaymentForm,
                        amount: selected.due.toString(),
                      });
                    }
                  }}
                >
                  <option value="">— Select an order —</option>
                  {orders.map((order) => (
                    <option key={order.orderId} value={order.orderId}>
                      {order.orderId} — {order.customerName}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              {/* Section 2: Order Summary */}
              {newPaymentOrderId && orders.find(o => o.orderId === newPaymentOrderId) && (() => {
                const selectedOrder = orders.find(o => o.orderId === newPaymentOrderId)!;
                return (
                  <SummaryBox>
                    <SummaryRow>
                      <SummaryLabel>Order</SummaryLabel>
                      <SummaryValue>{selectedOrder.orderId}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Customer</SummaryLabel>
                      <SummaryValue>{selectedOrder.customerName}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Order Total</SummaryLabel>
                      <SummaryValue>{formatCurrency(selectedOrder.total, selectedOrder.currency)}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Already Paid</SummaryLabel>
                      <SummaryValue>{formatCurrency(selectedOrder.total - selectedOrder.due, selectedOrder.currency)}</SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Due Amount</SummaryLabel>
                      <SummaryValue style={{ color: getOutstandingColor(selectedOrder.status, selectedOrder.due, theme.colors) }}>
                        {formatCurrency(selectedOrder.due, selectedOrder.currency)}
                      </SummaryValue>
                    </SummaryRow>
                    <SummaryRow>
                      <SummaryLabel>Currency</SummaryLabel>
                      <SummaryValue>{selectedOrder.currency}</SummaryValue>
                    </SummaryRow>
                  </SummaryBox>
                );
              })()}

              {/* Section 3: Payment Details */}
              
              {/* Payment constraints info */}
              {newPaymentOrderId && (() => {
                const selectedOrder = orders.find(o => o.orderId === newPaymentOrderId);
                if (!selectedOrder) return null;
                
                // Determine allowed methods based on currency
                const isINR = selectedOrder.currency === 'INR';
                const allowedMethods = isINR 
                  ? ['UPI', 'Bank Transfer', 'Cash', 'Check', 'Wallet']
                  : ['Wire Transfer', 'Credit Card', 'Bank Transfer'];
                const fxWarning = selectedOrder.currency !== 'INR';
                
                return (
                  <div style={{
                    marginBottom: 16,
                    padding: '12px',
                    background: theme.colors.infoLight,
                    borderRadius: 8,
                    border: `1px solid ${theme.colors.info}`
                  }}>
                    <div style={{ fontSize: '13px', fontWeight: 600, color: theme.colors.infoDark, marginBottom: 6 }}>
                      Payment Constraints
                    </div>
                    <div style={{ fontSize: '12px', color: theme.colors.gray700, marginBottom: 4 }}>
                      <strong>Settlement Currency:</strong> {selectedOrder.currency}
                      {fxWarning && (
                        <div style={{ color: theme.colors.warning, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                          <Icon name="AlertTriangle" size={12} />
                          FX conversion may apply for non-INR payments
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: '12px', color: theme.colors.gray700 }}>
                      <strong>Allowed Methods:</strong> {allowedMethods.join(', ')}
                    </div>
                  </div>
                );
              })()}

              <FormRow>
                <FormGroup>
                  <FormLabel>Payment Amount *</FormLabel>
                  <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
                    <span style={{ position: 'absolute', left: '12px', color: theme.colors.textSecondary, fontWeight: 600 }}>₹</span>
                    <Input
                      type="number"
                      placeholder="0.00"
                      value={newPaymentForm.amount}
                      onChange={(e) => setNewPaymentForm({ ...newPaymentForm, amount: e.target.value })}
                      style={{ paddingLeft: '32px' }}
                    />
                  {newPaymentOrderId && (() => {
                    const selectedOrder = orders.find(o => o.orderId === newPaymentOrderId);
                    const amt = parseFloat(newPaymentForm.amount || '0');
                    if (selectedOrder && amt > selectedOrder.due) {
                      return (
                        <div style={{ color: theme.colors.error, fontSize: '12px', marginTop: '4px' }}>
                          Amount cannot exceed outstanding balance ({formatCurrency(selectedOrder.due, selectedOrder.currency)})
                        </div>
                      );
                    }
                    return null;
                  })()}
                  </div>
                </FormGroup>

                <FormGroup>
                  <FormLabel>Payment Method *</FormLabel>
                  <Select
                    value={newPaymentForm.method}
                    onChange={(e) => {
                      // Clear method-specific fields when method changes
                      setNewPaymentForm({ 
                        ...newPaymentForm, 
                        method: e.target.value,
                        bankName: undefined,
                        transactionReference: undefined,
                        transferDate: undefined,
                        accountLast4: undefined,
                        cardType: undefined,
                        cardLast4: undefined,
                        authorizationId: undefined,
                        upiId: undefined,
                        walletName: undefined,
                        transactionId: undefined,
                        receivedBy: undefined,
                        receiptNumber: undefined,
                        receivedLocation: undefined,
                        checkNumber: undefined,
                      });
                    }}
                  >
                    <option value="Bank Transfer">Bank Transfer</option>
                    <option value="Credit Card">Credit Card</option>
                    <option value="Debit Card">Debit Card</option>
                    <option value="Wire Transfer">Wire Transfer</option>
                    <option value="UPI">UPI</option>
                    <option value="Wallet">Wallet</option>
                    <option value="Cash">Cash</option>
                    <option value="Check">Check</option>
                    <option value="System">System (Auto-settled)</option>
                  </Select>
                </FormGroup>
              </FormRow>

              {/* Method-specific fields */}
              <MethodSpecificFields
                method={newPaymentForm.method}
                formData={newPaymentForm as PaymentFormState}
                onFormChange={(data) => setNewPaymentForm({ ...newPaymentForm, ...data })}
              />

              <FormGroup>
                <FormLabel>Paid By *</FormLabel>
                <Select
                  value={newPaymentForm.paidBy}
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, paidBy: e.target.value as any })}
                >
                  <option value="Customer">Customer</option>
                  <option value="Agent">Agent</option>
                  <option value="Admin">Admin</option>
                </Select>
              </FormGroup>

              {/* Section 4: Metadata */}
              <FormRow>
                <FormGroup>
                  <FormLabel>Payment Date *</FormLabel>
                  <Input
                    type="date"
                    value={newPaymentForm.paymentDate}
                    onChange={(e) => setNewPaymentForm({ ...newPaymentForm, paymentDate: e.target.value })}
                  />
                </FormGroup>
              </FormRow>

              <FormGroup>
                <FormLabel>Notes (Optional)</FormLabel>
                <FormTextarea
                  placeholder="Add any notes about this payment..."
                  value={newPaymentForm.notes}
                  onChange={(e) => setNewPaymentForm({ ...newPaymentForm, notes: e.target.value })}
                />
              </FormGroup>

              <div style={{ fontSize: '12px', color: theme.colors.textSecondary, fontStyle: 'italic' }}>
                Initiated By: You (auto)
              </div>
            </ModalContent>

            <ModalFooter>
              <Button variant="ghost" onClick={() => setShowNewPayment(false)}>Cancel</Button>
              <Button
                disabled={isSubmittingPayment}
                onClick={async () => {
                  if (newPaymentOrderId && newPaymentForm.amount && !isSubmittingPayment) {
                    const selectedOrder = orders.find(o => o.orderId === newPaymentOrderId);
                    const amt = parseFloat(newPaymentForm.amount);
                    if (selectedOrder && amt > selectedOrder.due) {
                      return; // prevent submission if invalid
                    }

                    try {
                      setIsSubmittingPayment(true);
                      const amount = await persistPayment(newPaymentOrderId, newPaymentForm);
                      const updatedOutstanding = selectedOrder ? Math.max(selectedOrder.due - amount, 0) : undefined;
                      const message = selectedOrder && updatedOutstanding !== undefined
                        ? `Payment of ${formatCurrency(amount, selectedOrder.currency)} recorded. Outstanding balance: ${formatCurrency(updatedOutstanding, selectedOrder.currency)}`
                        : `Payment recorded.`;

                      setShowNewPayment(false);
                      setActivePaymentId(null);
                      setSelectedOrderId(newPaymentOrderId);
                      if (onPaymentSuccess) {
                        onPaymentSuccess(newPaymentOrderId, message);
                      }
                    } finally {
                      setIsSubmittingPayment(false);
                    }
                  }
                }}
              >
                {isSubmittingPayment ? 'Saving...' : 'Create Payment'}
              </Button>
            </ModalFooter>
          </ModalPanel>
        </ModalOverlay>
      )}

    </PaymentsContainer>
  );
};
